import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import {
  appApi,
  useGetStaffReportByIdQuery,
  useGetTechnicalEmployeesQuery,
} from "../../../store/appApi";
import { selectUser, selectUserRole } from "../../../store/authSlice";
import {
  createCustomerDealerEntry,
  mergeMasterIntoContacts,
  createFieldSubWork,
  createMainWork,
  createMetricSet,
  createOtherWork,
  deriveReportingSummary,
  FIELD_STATUS_OPTIONS,
  formatDateLabel,
  formatRoleLabel,
  addDailyStatText,
  getDailyStat,
  getDefaultFormData,
  getMetricEntryValue,
  getNetplusEntry,
  getWorkOverdueInfo,
  groupRemarksByDate,
  isTextMetricKey,
  istDayKey,
  isWorkComplete,
  isWorkVerified,
  isWorkOnHold,
  NETPLUS_STATUS_FIELDS,
  NETPLUS_MGR_FIELDS,
  withNetplusMgrRunningTotals,
  normalizeFieldReport,
  normalizeOtherWorks,
  normalizeRemarksHistory,
  normalizeSubWorkUpdates,
  sortSubWorkUpdates,
  TECHNICAL_ROLE_OPTIONS,
  toDateInputValue,
  toLegacyFieldMirror,
  upsertDailyStat,
  upsertMetricEntries,
  upsertNetplusEntry,
  workBadgeToneClass,
} from "./reportingConfig";
import {
  deleteStaffReportWorkApi,
  editStaffReportWorkUpdateApi,
  getFieldEmployeesApi,
  transferStaffReportApi,
  holdStaffReportWorkApi,
  resumeStaffReportWorkApi,
  updateStaffReportApi,
  verifyStaffReportWorkApi,
} from "../../../services/staffReporting";
import { exportStaffReportToExcel } from "./exportStaffReportExcel";

const panelClass =
  "rounded-3xl border border-gray-200 bg-white shadow-xl shadow-slate-200/70";
const inputClass =
  "w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20";
const labelClass =
  "mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-gray-500";

// Deadline pill: red once a deadline is crossed with the work still open,
// amber when it was finished late or is due within 3 days, slate while it's
// comfortably on track. `getWorkOverdueInfo` returns null when no deadline is
// set and no label for a job finished on time.
const OverdueBadge = ({ work }) => {
  const info = getWorkOverdueInfo(work);
  if (!info || !info.label) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${workBadgeToneClass(
        info.state,
      )}`}
    >
      <i
        className={`fa-solid ${
          info.state === "overdue" || info.state === "completed-late"
            ? "fa-triangle-exclamation"
            : "fa-clock"
        } text-[10px]`}
      />
      {info.label}
    </span>
  );
};

const API_ORIGIN = process.env.REACT_APP_API_URL || "http://localhost:5001";
const resolveUrl = (u) =>
  !u ? null : u.startsWith("http") ? u : `${API_ORIGIN}${u}`;

// Green pill once an admin has signed off on a completed work.
const VerifiedBadge = ({ work }) => {
  if (!isWorkVerified(work)) return null;
  const { verifiedByName, verifiedAt, remarks } = work.verification;
  const title = [
    verifiedByName && `By ${verifiedByName}`,
    verifiedAt && formatDateLabel(verifiedAt),
    remarks && `“${remarks}”`,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <span
      title={title || undefined}
      className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-200"
    >
      <i className="fa-solid fa-circle-check text-[10px]" />
      Verified
    </span>
  );
};

// Professional "Well done" badge — the work was finished on or before its
// deadline and an admin has verified it.
const WellDoneBadge = ({ work }) => {
  const info = getWorkOverdueInfo(work);
  if (!info?.wellDone) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-emerald-500 to-green-600 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-[0.12em] text-white shadow-sm">
      <i className="fa-solid fa-award text-[10px]" />
      Well Done
    </span>
  );
};

// Blue pill while a work is paused — its deadline countdown is frozen and it
// has no effect on performance review until resumed.
const OnHoldBadge = ({ work }) => {
  if (!isWorkOnHold(work)) return null;
  return (
    <span
      title={work.holdReason ? `“${work.holdReason}”` : undefined}
      className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-0.5 text-[11px] font-semibold text-blue-700 ring-1 ring-blue-200"
    >
      <i className="fa-solid fa-pause text-[10px]" />
      On Hold
    </span>
  );
};

const DetailStaffReporting = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: reportId } = useParams();
  const userRole = useSelector(selectUserRole);
  const isSuperAdmin = userRole === "super-admin";
  const currentUser = useSelector(selectUser);
  const currentUserName =
    currentUser?.name || currentUser?.username || currentUser?.email || "Admin";

  const { data: reportDetailData, isLoading: reportLoading } =
    useGetStaffReportByIdQuery(reportId, {
      skip: !reportId,
    });

  const selectedReport = reportDetailData?.report || null;
  // Persistent customer roster for Tele Calling / Real Estate Sales — the
  // period report's contact rows are always derived from this.
  const masterCustomers = Array.isArray(reportDetailData?.masterCustomers)
    ? reportDetailData.masterCustomers
    : [];
  const employee = selectedReport?.employeeId || null;
  const employeeName =
    employee?.name || selectedReport?.employeeName || "Staff member";
  const employeeRole = employee?.role || selectedReport?.employeeRole || "";
  // Admin verification + deadline-extension are only for the technical field
  // roles (Project Manager / Supervisor / Civil Engineer) — not Foreman or any
  // non-formType-1 role.
  const isTechnicalFieldRole = TECHNICAL_ROLE_OPTIONS.includes(employeeRole);
  const canAdminWorks = isSuperAdmin && isTechnicalFieldRole;
  // A Project Manager reviewing a Supervisor / Foreman / Civil Engineer's
  // report (the backend only returns those) can verify, hold and resume their
  // work like a super-admin; their own report stays a normal editable one.
  const isReviewingTeamReport =
    userRole === "Project Manager" &&
    Boolean(employee?._id) &&
    String(employee._id) !== String(currentUser?.localId || "");
  const isReviewer = isSuperAdmin || isReviewingTeamReport;
  const canVerifyWorks = canAdminWorks || isReviewingTeamReport;
  // The employee (or a super-admin) can correct / delete a wrongly-added daily
  // update; a PM reviewing a team member's sheet cannot.
  const canEditWorkUpdates =
    isSuperAdmin || (!isReviewingTeamReport && !selectedReport?.isComplete);
  // Role: Revenue — its Field Execution Journal keeps the same look as the
  // technical roles', with two tweaks: a main work with no sub-works is
  // updatable directly, and a main work's status rolls up from its sub-works
  // (all Complete → the main work shows Complete). Other roles are untouched.
  const isRevenueReport = employeeRole === "Revenue";

  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Field Execution Journal (formType 1) — a work's remarks card only shows
  // the current (latest) update inline; this holds the full date-wise list
  // for whichever work's "View all" button was clicked, shown in a modal.
  const [remarksModal, setRemarksModal] = useState(null);
  // The daily update being corrected in that modal: { key, text, people }.
  const [updateEdit, setUpdateEdit] = useState(null);
  // Netplus Updates (formType 7) — which day the area-count editor is writing.
  const [netplusDate, setNetplusDate] = useState(toDateInputValue());
  const [selectedTaskIndex, setSelectedTaskIndex] = useState(0);
  const [taskEdits, setTaskEdits] = useState({
    status: "Pending",
    remarks: "",
    date: toDateInputValue(),
  });
  // Revenue (formType 6) task quick-editor can target either the whole task
  // ("") or one of its nested sub-works (index as a string) — sub-works get
  // their own status + dated remarks history, separate from the task's.
  const [selectedSubWorkIndex, setSelectedSubWorkIndex] = useState("");
  // Revenue sheets (formType 6) get a permanent "Other Work" list — free-text
  // entries unrelated to the task table, editable independently of it.
  const [otherWorks, setOtherWorks] = useState([]);
  // Field Execution Journal (formType 1) quick-editor — cascading
  // Unit → Main Work → Sub Work pickers. The Unit is fixed per sheet; the
  // Main Work / Sub Work ids drive which nested sub-work the status + dated
  // daily-work note (with its head-count) is written back to.
  const [fieldMainWorkId, setFieldMainWorkId] = useState("");
  const [fieldSubWorkId, setFieldSubWorkId] = useState("");
  const [fieldEdits, setFieldEdits] = useState({
    status: "Pending",
    remarks: "",
    peopleCount: "",
    date: toDateInputValue(),
  });
  // Real Estate Sales (formType 4) quick-editor: which "<Project> Site Visit"
  // / "Open Visit" row the Value field is currently pointed at.
  const [salesMetricTarget, setSalesMetricTarget] = useState("");
  // Which date the Value field reads/writes — defaults to today but can be
  // moved back so a missed day's Site Visit / Open Visit count can be logged
  // after the fact, same as the Working Date picker on the Create page.
  const [salesEntryDate, setSalesEntryDate] = useState(toDateInputValue());

  // Tele Calling (3) & Real Estate Sales (4) contacts/calls are managed on the
  // Customer Database page now — this page only shows a read-only breakdown
  // (see renderContactBreakdown / renderSheetBreakdown).
  // Which day the Visits / Sales inputs read & write (Real Estate Sales), plus
  // a live draft of what's typed — cleared to blank after each save.
  const [statsDate, setStatsDate] = useState(toDateInputValue());
  const [dailyDraft, setDailyDraft] = useState({});
  // Which day the fixed calling-metric inputs read & write (Tele Calling), plus
  // the typed draft — cleared after each save.
  const [metricsDate, setMetricsDate] = useState(toDateInputValue());
  const [metricDraft, setMetricDraft] = useState({});

  const { data: technicalData } = useGetTechnicalEmployeesQuery();
  const technicalStaffById = new Map(
    (Array.isArray(technicalData?.staff) ? technicalData.staff : []).map(
      (person) => [person._id, person],
    ),
  );

  const reportFormType = Number(selectedReport?.formType) || 0;

  // Field Execution Journal (formType 1) — Unit → Main Works → Sub Works tree,
  // legacy flat sheets folded in. Memoized because normalizeFieldReport mints
  // fresh row ids for legacy sheets on every call: the quick-editor stores a
  // main-work / sub-work id in state, so that id set must stay stable across
  // renders or the editor can never match its own selection.
  const fieldReport = useMemo(
    () =>
      reportFormType === 1
        ? normalizeFieldReport(selectedReport?.formData)
        : { unitName: "", mainWorks: [] },
    [reportFormType, selectedReport?.formData],
  );
  useEffect(() => {
    if (!selectedReport) return;
    const baseForm = getDefaultFormData(
      selectedReport.formType,
      selectedReport.reportDate,
    );
    const formType = Number(selectedReport.formType);
    const nextForm = {
      ...baseForm,
      ...selectedReport.formData,
      status: selectedReport.status || baseForm.status || "Pending",
      remarks: selectedReport.formData?.remarks || "",
    };
    // Tele Calling / Real Estate Sales — the contact rows are always the
    // current master roster (carrying over any calls already logged here).
    if (formType === 3 || formType === 4) {
      nextForm.contacts = mergeMasterIntoContacts(
        masterCustomers,
        selectedReport.formData?.contacts,
        toDateInputValue(),
      );
    }
    // Tele Calling — normalise the fixed calling-metric rows to the current
    // label set (adds "Extra Work" etc. to older sheets), keeping saved values
    // by key so nothing is lost.
    if (formType === 3) {
      const saved = new Map(
        (Array.isArray(selectedReport.formData?.metrics)
          ? selectedReport.formData.metrics
          : []
        ).map((m) => [m.key, m.entries]),
      );
      nextForm.metrics = createMetricSet(3).map((m) => ({
        ...m,
        entries: Array.isArray(saved.get(m.key)) ? saved.get(m.key) : [],
      }));
    }
    setEditForm(nextForm);

    if (formType === 1) {
      const field = fieldReport;
      const firstMain = field.mainWorks[0];
      const firstSub = firstMain?.subWorks?.[0];
      setFieldMainWorkId(firstMain?.id || "");
      setFieldSubWorkId(firstSub?.id || "");
      setFieldEdits({
        status: firstSub?.status || firstMain?.status || "Pending",
        remarks: "",
        peopleCount: "",
        date: toDateInputValue(),
      });
    }
    if (formType === 5 || formType === 6) {
      const tasks = Array.isArray(selectedReport.formData?.tasks)
        ? selectedReport.formData.tasks
        : [];
      setSelectedTaskIndex(0);
      setTaskEdits({
        status: tasks[0]?.status || "Pending",
        // Revenue's Remarks box is for adding a new dated note, not editing
        // the last one, so it always starts blank.
        remarks: formType === 6 ? "" : tasks[0]?.remarks || "",
        date: toDateInputValue(),
      });
    }
    if (formType === 6) {
      const savedOtherWorks = normalizeOtherWorks(
        selectedReport.formData?.otherWorks,
      );
      setOtherWorks(savedOtherWorks.length ? savedOtherWorks : [createOtherWork()]);
    }
    if (formType === 4) {
      const metrics = Array.isArray(selectedReport.formData?.metrics)
        ? selectedReport.formData.metrics
        : [];
      setSalesMetricTarget(metrics[0]?.key || "");
      setSalesEntryDate(toDateInputValue());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReport, fieldReport, reportDetailData?.masterCustomers]);

  const handleEditField = (key, value) => {
    setEditForm((prev) => ({ ...prev, [key]: value }));
  };

  // Netplus Updates (formType 7) — write one status count for one area on
  // `netplusDate`, keeping that area's other four counts for the day intact.
  const handleNetplusEditValue = (code, fieldKey, value) => {
    setEditForm((prev) => {
      const areas = Array.isArray(prev.areas) ? prev.areas : [];
      const area = areas.find((a) => String(a.code) === String(code));
      const current = getNetplusEntry(area, netplusDate);
      const valuesByKey = {};
      NETPLUS_STATUS_FIELDS.forEach(({ key }) => {
        valuesByKey[key] = key === fieldKey ? value : current[key] ?? "";
      });
      return {
        ...prev,
        areas: upsertNetplusEntry(areas, netplusDate, code, valuesByKey),
      };
    });
  };

  // Updates salesEntryDate's value for whichever project's Site Visit row (or
  // Open Visit) is currently selected in the quick editor — every other row
  // and every other date's entry is left untouched.
  const handleSalesValueChange = (value) => {
    if (!salesMetricTarget) return;
    setEditForm((prev) => ({
      ...prev,
      metrics: upsertMetricEntries(prev.metrics, salesEntryDate, {
        [salesMetricTarget]: value,
      }),
    }));
  };

  // Real Estate Sales (formType 4) — optional customer/dealer contact log,
  // edited in place on this page and persisted via handleSave (it is carried
  // inside editForm, so the generic save picks it up).
  const handleAddCustomerDealer = () => {
    setEditForm((prev) => ({
      ...prev,
      customerDealerLog: [
        ...(Array.isArray(prev.customerDealerLog)
          ? prev.customerDealerLog
          : []),
        createCustomerDealerEntry(),
      ],
    }));
  };

  const handleUpdateCustomerDealer = (index, field, value) => {
    setEditForm((prev) => ({
      ...prev,
      customerDealerLog: (Array.isArray(prev.customerDealerLog)
        ? prev.customerDealerLog
        : []
      ).map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    }));
  };

  const handleRemoveCustomerDealer = (index) => {
    setEditForm((prev) => ({
      ...prev,
      customerDealerLog: (Array.isArray(prev.customerDealerLog)
        ? prev.customerDealerLog
        : []
      ).filter((_, i) => i !== index),
    }));
  };

  // ── Tele Calling (3) & Real Estate Sales (4) — contact + call-log handlers ─
  // Each of these updates editForm locally AND persists the sheet immediately
  // (adding a contact / logging a call is a discrete action, not a draft), so
  // they push the freshly-built form straight to the API rather than relying
  // on the generic Save button.
  const persistReportForm = async (nextForm, successText) => {
    if (!selectedReport?._id) return false;
    if (selectedReport.isComplete) {
      Swal.fire({
        icon: "info",
        title: "Read only",
        text: "Completed sheets cannot be edited.",
      });
      return false;
    }
    try {
      setSaving(true);
      await updateStaffReportApi(selectedReport._id, {
        ...nextForm,
        reportDate: selectedReport.reportDate,
        date: selectedReport.reportDate,
        status: nextForm.status || selectedReport.status || "Pending",
        remarks: nextForm.remarks || "",
      });
      dispatch(appApi.util.invalidateTags(["WorkProgressReports"]));
      Swal.fire({
        icon: "success",
        title: "Saved",
        text: successText || "The sheet has been updated.",
        timer: 1400,
        showConfirmButton: false,
      });
      return true;
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to save",
        text: error?.response?.data?.message || "Could not update the sheet.",
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Real Estate Sales manual daily numbers — typed into a draft that is
  // committed to editForm.dailyStats on Save, then cleared to blank.
  const handleDailyStatField = (field, value) =>
    setDailyDraft((prev) => ({
      ...prev,
      [field]: String(value),
    }));

  // Switch the entry day — load that day's saved numbers into the draft so an
  // Just switch the entry day — the inputs stay blank (each Save adds to that
  // day's running total).
  const handleStatsDateChange = (date) => {
    if (!date) return;
    setStatsDate(date);
    setDailyDraft({});
  };

  const handleSaveDailyStats = async () => {
    const typed = {};
    ["openVisits", "siteVisits", "calls", "sales", "remarks"].forEach((k) => {
      const text = String(dailyDraft[k] ?? "").trim();
      if (text) typed[k] = text;
    });
    if (!Object.keys(typed).length) {
      Swal.fire({
        icon: "info",
        title: "Nothing to save",
        text: "Enter at least one value first.",
        timer: 1400,
        showConfirmButton: false,
      });
      return;
    }
    // ADD the typed values to whatever is already recorded for that day, so
    // logging activity in batches through the day accumulates (counts add up,
    // notes are joined — see addDailyStatText).
    const row = getDailyStat(editForm.dailyStats, statsDate);
    const patch = {};
    Object.entries(typed).forEach(([k, text]) => {
      // Remarks are appended to the day's earlier remarks, not counted.
      patch[k] =
        k === "remarks"
          ? [String(row.remarks || "").trim(), text].filter(Boolean).join("; ")
          : addDailyStatText(row[k], text);
    });
    const nextForm = {
      ...editForm,
      dailyStats: upsertDailyStat(editForm.dailyStats, statsDate, patch),
    };
    setEditForm(nextForm);
    const ok = await persistReportForm(nextForm, "Daily numbers added.");
    if (ok) setDailyDraft({});
  };

  // Tele Calling — typed calling-metric values, committed on Save then cleared.
  // "Data Maintained" / "Extra Work" are free text; the rest are digit counts.
  const handleMetricField = (key, value) =>
    setMetricDraft((prev) => ({
      ...prev,
      [key]: isTextMetricKey(3, key)
        ? value
        : String(value).replace(/[^0-9]/g, ""),
    }));

  const handleMetricsDateChange = (date) => {
    if (!date) return;
    setMetricsDate(date);
    setMetricDraft({});
  };

  const handleSaveMetrics = async () => {
    const rows = Array.isArray(editForm.metrics) ? editForm.metrics : [];
    const byKey = new Map(rows.map((m) => [m.key, m]));
    const patch = {};
    Object.entries(metricDraft).forEach(([k, v]) => {
      if (isTextMetricKey(3, k)) {
        // Text remark — replace the day's value (blank clears it).
        if (v !== undefined) patch[k] = String(v).trim();
      } else {
        const n = Number(v);
        if (Number.isFinite(n) && n > 0) {
          const prev =
            Number(getMetricEntryValue(byKey.get(k) || {}, metricsDate)) || 0;
          patch[k] = prev + n; // ADD to the day's running count
        }
      }
    });
    if (!Object.keys(patch).length) {
      Swal.fire({
        icon: "info",
        title: "Nothing to save",
        text: "Enter a value or a remark first.",
        timer: 1400,
        showConfirmButton: false,
      });
      return;
    }
    const nextForm = {
      ...editForm,
      metrics: upsertMetricEntries(editForm.metrics, metricsDate, patch),
    };
    setEditForm(nextForm);
    const ok = await persistReportForm(nextForm, "Calling metrics saved.");
    if (ok) setMetricDraft({});
  };

  const handleSelectTask = (index) => {
    setSelectedTaskIndex(index);
    setSelectedSubWorkIndex("");
    const tasks = Array.isArray(selectedReport?.formData?.tasks)
      ? selectedReport.formData.tasks
      : [];
    const task = tasks[index];
    const formType = Number(selectedReport?.formType);
    setTaskEdits({
      status: task?.status || "Pending",
      remarks: formType === 6 ? "" : task?.remarks || "",
      date: toDateInputValue(),
    });
  };

  const handleSelectSubWork = (value) => {
    setSelectedSubWorkIndex(value);
    const tasks = Array.isArray(selectedReport?.formData?.tasks)
      ? selectedReport.formData.tasks
      : [];
    const task = tasks[selectedTaskIndex];
    const subWorks = Array.isArray(task?.subWorks) ? task.subWorks : [];
    const target = value === "" ? task : subWorks[Number(value)];
    setTaskEdits((prev) => ({
      ...prev,
      status: target?.status || "Pending",
      remarks: "",
    }));
  };

  const handleEditTaskField = (key, value) => {
    setTaskEdits((prev) => ({ ...prev, [key]: value }));
  };

  // ─── Field Execution Journal (formType 1) cascading quick-editor ──────────
  const handleSelectFieldMainWork = (id) => {
    const field = fieldReport;
    const main = field.mainWorks.find((mainWork) => mainWork.id === id);
    const firstSub = main?.subWorks?.[0];
    setFieldMainWorkId(id);
    setFieldSubWorkId(firstSub?.id || "");
    setFieldEdits((prev) => ({
      ...prev,
      status: firstSub?.status || main?.status || "Pending",
      remarks: "",
      peopleCount: "",
    }));
  };

  const handleSelectFieldSubWork = (id) => {
    const field = fieldReport;
    const main = field.mainWorks.find(
      (mainWork) => mainWork.id === fieldMainWorkId,
    );
    const sub = (main?.subWorks || []).find((subWork) => subWork.id === id);
    setFieldSubWorkId(id);
    setFieldEdits((prev) => ({
      ...prev,
      status: sub?.status || "Pending",
      remarks: "",
      peopleCount: "",
    }));
  };

  const handleEditFieldEditsField = (key, value) => {
    setFieldEdits((prev) => ({ ...prev, [key]: value }));
  };

  // Low-level save: persist a full Field Execution Journal mainWorks tree for
  // this sheet (with the legacy flat mirror refreshed) and roll the sheet's
  // overall status. Every structural / status change on this page funnels
  // through here. Returns true on success.
  const commitFieldMainWorks = async (mainWorks, successText, options = {}) => {
    if (!selectedReport?._id) return false;
    if (selectedReport.isComplete) {
      Swal.fire({
        icon: "info",
        title: "Read only",
        text: "Completed sheets cannot be edited.",
      });
      return false;
    }

    // The sheet is only "Complete" once every sub-work across every main work
    // is — never from a single status dropdown; and if it was left marked
    // Complete while a sub-work is not, roll it back to In Progress.
    // Revenue: a main work with no sub-works contributes its own status.
    const allRows = mainWorks.flatMap((main) =>
      (main.subWorks || []).length
        ? main.subWorks
        : isRevenueReport
          ? [{ status: main.status }]
          : [],
    );
    const allComplete =
      allRows.length > 0 &&
      allRows.every((sub) => /complete/i.test(String(sub.status || "")));
    let overallStatus = selectedReport.status || "Pending";
    if (allComplete) {
      overallStatus = "Complete";
    } else if (/^complete(d)?$/i.test(String(overallStatus || "").trim())) {
      overallStatus = "In Progress";
    }

    // The unit name can be corrected from this page (e.g. legacy sheets that
    // were saved without one); fall back to the sheet's current name.
    const unitName =
      options.unitName !== undefined
        ? String(options.unitName).trim()
        : fieldReport.unitName;

    try {
      setSaving(true);
      const payload = {
        unitName,
        mainWorks,
        // Refresh the flat legacy mirror so the not-yet-migrated
        // EmployeeReports / Excel views keep rendering this sheet.
        ...toLegacyFieldMirror({ unitName, mainWorks }),
        reportDate: selectedReport.reportDate,
        date: selectedReport.reportDate,
        status: overallStatus,
        remarks: selectedReport.formData?.remarks || "",
      };

      await updateStaffReportApi(selectedReport._id, payload);
      dispatch(appApi.util.invalidateTags(["WorkProgressReports"]));
      Swal.fire({
        icon: "success",
        title: "Saved",
        text: successText || "The sheet has been updated.",
        timer: 1500,
        showConfirmButton: false,
      });
      return true;
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to save",
        text: error?.response?.data?.message || "Could not update the sheet.",
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Shared save path for both the top cascading quick-editor and the per-row
  // editors in the Sheet Breakdown: writes `status` onto the targeted
  // sub-work and appends a dated { date, text, peopleCount } entry to its
  // update log whenever a note / head-count is supplied OR the status itself
  // moved — a bare status change (e.g. Pending → Complete) must still show in
  // the update trail instead of leaving "No updates yet".
  const persistFieldSubWork = async ({
    mainWorkId,
    subWorkId,
    status,
    date,
    remarks,
    peopleCount,
  }) => {
    const field = fieldReport;
    if (!field.mainWorks.length || !mainWorkId || !subWorkId) return false;

    const noteDate = date || toDateInputValue();
    const noteText = String(remarks || "").trim();
    const rawPeople = String(peopleCount ?? "").trim();
    const people = rawPeople === "" ? "" : Math.max(0, Number(rawPeople) || 0);

    const mainWorks = field.mainWorks.map((main) => {
      if (main.id !== mainWorkId) return main;
      const subWorks = (main.subWorks || []).map((sub) => {
        if (sub.id !== subWorkId) return sub;
        const history = normalizeSubWorkUpdates(sub.updatedRemarks);
        const nextStatus = status || sub.status || "Pending";
        const statusChanged =
          String(nextStatus).trim().toLowerCase() !==
          String(sub.status || "Pending").trim().toLowerCase();
        const shouldAppend = Boolean(noteText) || people !== "" || statusChanged;
        const entryText =
          noteText || (statusChanged ? `Status set to ${nextStatus}` : "");
        const wasComplete = /complete|completed|done/i.test(sub.status || "");
        const nowComplete = /complete|completed|done/i.test(nextStatus);
        // Stamp the real finish date the moment it's marked Complete (using the
        // Date the reporter picked, so a back-dated finish is honoured); clear
        // it if the status moves back off Complete.
        const completedOn = nowComplete
          ? wasComplete
            ? sub.completedOn || noteDate
            : noteDate
          : "";
        return {
          ...sub,
          status: nextStatus,
          completedOn,
          updatedRemarks: shouldAppend
            ? [
                ...history,
                { date: noteDate, text: entryText, peopleCount: people },
              ]
            : history,
        };
      });
      // Revenue: keep the main work's own status in sync with its sub-works so
      // lists that read main.status (EmployeeReports, Excel) reflect reality —
      // all sub-works Complete → the main work is Complete.
      let mainStatus = main.status || "Pending";
      if (isRevenueReport && subWorks.length) {
        if (
          subWorks.every((s) => /complete/i.test(String(s.status || "")))
        ) {
          mainStatus = "Complete";
        } else if (
          subWorks.some((s) =>
            /complete|progress/i.test(String(s.status || "")),
          )
        ) {
          mainStatus = "In Progress";
        } else {
          mainStatus = "Pending";
        }
      }
      return { ...main, status: mainStatus, subWorks };
    });

    return commitFieldMainWorks(mainWorks, "The sub-work has been updated.");
  };

  const handleSaveField = () =>
    persistFieldSubWork({
      mainWorkId: fieldMainWorkId,
      subWorkId: fieldSubWorkId,
      status: fieldEdits.status,
      date: fieldEdits.date,
      remarks: fieldEdits.remarks,
      peopleCount: fieldEdits.peopleCount,
    });

  // ── Add structure to an existing sheet — no new sheet per unit needed ──
  const [newMainWorkName, setNewMainWorkName] = useState("");
  const [newSubWorkNames, setNewSubWorkNames] = useState({});

  // Unit name can be set / corrected in place (legacy sheets often have none).
  const [editingUnit, setEditingUnit] = useState(false);
  const [unitNameDraft, setUnitNameDraft] = useState("");

  const startEditUnit = () => {
    setUnitNameDraft(fieldReport.unitName || "");
    setEditingUnit(true);
  };

  const handleSaveUnitName = async () => {
    const name = unitNameDraft.trim();
    if (!name) return;
    const ok = await commitFieldMainWorks(
      fieldReport.mainWorks,
      "Unit name updated.",
      { unitName: name },
    );
    if (ok) setEditingUnit(false);
  };

  const handleAddFieldMainWork = async () => {
    const name = newMainWorkName.trim();
    if (!name) return;
    const newMain = {
      ...createMainWork(toDateInputValue()),
      mainWorkName: name,
      subWorks: [],
    };
    const ok = await commitFieldMainWorks(
      [...fieldReport.mainWorks, newMain],
      "Main work added.",
    );
    if (ok) setNewMainWorkName("");
  };

  const handleAddFieldSubWork = async (mainWorkId) => {
    const name = String(newSubWorkNames[mainWorkId] || "").trim();
    if (!name) return;
    const newSub = { ...createFieldSubWork(toDateInputValue()), subWorkName: name };
    const mainWorks = fieldReport.mainWorks.map((main) =>
      main.id === mainWorkId
        ? { ...main, subWorks: [...(main.subWorks || []), newSub] }
        : main,
    );
    const ok = await commitFieldMainWorks(mainWorks, "Sub-work added.");
    if (ok)
      setNewSubWorkNames((prev) => ({ ...prev, [mainWorkId]: "" }));
  };

  // Per-row inline editors in the Sheet Breakdown — one independent draft per
  // sub-work id, so an employee can update any sub-work directly from its row.
  const [rowEdits, setRowEdits] = useState({});
  const getRowEdit = (sub) =>
    rowEdits[sub.id] || {
      status: sub.status || "Pending",
      date: toDateInputValue(),
      peopleCount: "",
      remarks: "",
    };
  const setRowEditField = (subId, key, value) =>
    setRowEdits((prev) => ({
      ...prev,
      [subId]: { ...(prev[subId] || {}), [key]: value },
    }));
  const handleSaveSubWorkRow = async (mainWorkId, sub) => {
    const edit = getRowEdit(sub);
    const ok = await persistFieldSubWork({
      mainWorkId,
      subWorkId: sub.id,
      status: edit.status,
      date: edit.date,
      remarks: edit.remarks,
      peopleCount: edit.peopleCount,
    });
    if (ok) {
      // Drop the draft so the row re-derives from the freshly-saved data.
      setRowEdits((prev) => {
        const next = { ...prev };
        delete next[sub.id];
        return next;
      });
    }
  };

  // Update a MAIN work directly — only reachable when it has no sub-works of
  // its own (Revenue sheets). Writes status + an append-only dated note onto
  // the main work, same shape as a sub-work update.
  const handleSaveMainWorkRow = async (main) => {
    const edit = getRowEdit(main);
    const noteDate = edit.date || toDateInputValue();
    const noteText = String(edit.remarks || "").trim();
    const rawPeople = String(edit.peopleCount ?? "").trim();
    const people = rawPeople === "" ? "" : Math.max(0, Number(rawPeople) || 0);

    const mainWorks = fieldReport.mainWorks.map((m) => {
      if (m.id !== main.id) return m;
      const history = normalizeSubWorkUpdates(m.updatedRemarks);
      const nextStatus = edit.status || m.status || "Pending";
      const statusChanged =
        String(nextStatus).trim().toLowerCase() !==
        String(m.status || "Pending").trim().toLowerCase();
      const shouldAppend = Boolean(noteText) || people !== "" || statusChanged;
      const entryText =
        noteText || (statusChanged ? `Status set to ${nextStatus}` : "");
      const wasComplete = /complete|completed|done/i.test(m.status || "");
      const nowComplete = /complete|completed|done/i.test(nextStatus);
      const completedOn = nowComplete
        ? wasComplete
          ? m.completedOn || noteDate
          : noteDate
        : "";
      return {
        ...m,
        status: nextStatus,
        completedOn,
        updatedRemarks: shouldAppend
          ? [
              ...history,
              { date: noteDate, text: entryText, peopleCount: people },
            ]
          : history,
      };
    });

    const ok = await commitFieldMainWorks(
      mainWorks,
      "The work has been updated.",
    );
    if (ok) {
      setRowEdits((prev) => {
        const next = { ...prev };
        delete next[main.id];
        return next;
      });
    }
  };

  // ── Admin editing of a work's deadline / extension ────────────────────────
  // One draft per main/sub work id: the original completionDate (correctable)
  // and an extendedDate (a push-out that keeps the original for the delay
  // maths). Admins only.
  const [dateEdits, setDateEdits] = useState({});
  const getDateEdit = (work) =>
    dateEdits[work.id] || {
      completionDate: work.completionDate || "",
      extendedDate: work.extendedDate || "",
      // Reason + employee-fault flag for the extension the admin is about to
      // record. Pre-filled from the last extension so re-saving keeps them.
      reason: work.extensionReason || "",
      dueToEmployee: Boolean(work.extendedDueToEmployee),
    };
  const setDateEditField = (work, key, value) =>
    setDateEdits((prev) => ({
      ...prev,
      [work.id]: { ...getDateEdit(work), ...(prev[work.id] || {}), [key]: value },
    }));
  const handleSaveWorkDates = async ({ mainId, subId, work }) => {
    const edit = getDateEdit(work);
    const completionDate = edit.completionDate || "";
    let extendedDate = edit.extendedDate || "";
    const reason = String(edit.reason || "").trim();
    const dueToEmployee = Boolean(edit.dueToEmployee);
    // An extension must be a real push-out past the original date.
    if (
      extendedDate &&
      completionDate &&
      new Date(extendedDate) <= new Date(completionDate)
    ) {
      Swal.fire({
        icon: "warning",
        title: "Check the extended date",
        text: "The extended date must be after the original completion date.",
      });
      return;
    }

    // Is this a NEW extension (a different push-out date from what's stored)?
    const prevExtended = work.extendedDate || "";
    const isNewExtension = Boolean(extendedDate) && extendedDate !== prevExtended;
    if (isNewExtension && !reason) {
      Swal.fire({
        icon: "warning",
        title: "Add a reason",
        text: "Enter why this deadline is being extended before saving.",
      });
      return;
    }

    const priorExtensions = Array.isArray(work.extensions)
      ? work.extensions
      : [];
    const nextExtensions = isNewExtension
      ? [
          ...priorExtensions,
          {
            date: extendedDate,
            reason,
            dueToEmployee,
            extendedByName: currentUserName,
            extendedAt: new Date().toISOString(),
          },
        ]
      : priorExtensions;

    const patch = {
      completionDate,
      extendedDate,
      // Keep the latest reason / flag alongside the append-only trail.
      extensionReason: extendedDate ? reason : "",
      extendedDueToEmployee: extendedDate ? dueToEmployee : false,
      extensions: nextExtensions,
    };
    const mainWorks = fieldReport.mainWorks.map((main) => {
      if (main.id !== mainId) return main;
      if (!subId) return { ...main, ...patch };
      return {
        ...main,
        subWorks: (main.subWorks || []).map((sub) =>
          sub.id === subId ? { ...sub, ...patch } : sub,
        ),
      };
    });
    const ok = await commitFieldMainWorks(
      mainWorks,
      "Deadline updated.",
    );
    if (ok)
      setDateEdits((prev) => {
        const next = { ...prev };
        delete next[work.id];
        return next;
      });
  };

  // ── Admin verification of a completed work ─────────────────────────────────
  // One draft per main/sub work id: the "Verified" checkbox + the admin's
  // remark, submitted together and stamped with the admin's name and date.
  const [verifyEdits, setVerifyEdits] = useState({});
  const getVerifyEdit = (work) =>
    verifyEdits[work.id] || {
      verified: isWorkVerified(work),
      remarks: work.verification?.remarks || "",
    };
  const setVerifyEditField = (work, key, value) =>
    setVerifyEdits((prev) => ({
      ...prev,
      [work.id]: { ...getVerifyEdit(work), ...(prev[work.id] || {}), [key]: value },
    }));
  const handleSubmitVerification = async ({ mainId, subId, work }) => {
    if (!selectedReport?._id) return;
    const edit = getVerifyEdit(work);
    try {
      setSaving(true);
      // Multipart, so the optional site photo can ride along.
      const fd = new FormData();
      fd.append("mainId", mainId);
      if (subId) fd.append("subId", subId);
      fd.append("verified", String(Boolean(edit.verified)));
      fd.append("remarks", String(edit.remarks || "").trim());
      if (edit.photo && edit.verified) fd.append("photo", edit.photo);
      await verifyStaffReportWorkApi(selectedReport._id, fd);
      dispatch(appApi.util.invalidateTags(["WorkProgressReports"]));
      Swal.fire({
        icon: "success",
        title: "Saved",
        text: edit.verified
          ? "Work marked verified."
          : "Verification cleared.",
        timer: 1500,
        showConfirmButton: false,
      });
      setVerifyEdits((prev) => {
        const next = { ...prev };
        delete next[work.id];
        return next;
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to save",
        text:
          error?.response?.data?.message || "Could not verify the work.",
      });
    } finally {
      setSaving(false);
    }
  };

  // ── Self-service Hold / Resume ──────────────────────────────────────────────
  // One draft per work id: the hold reason while pausing, or the new
  // completion date while resuming. Either the employee or an admin may do
  // this (backend enforces ownership) — no canAdminWorks gate here.
  const [holdEdits, setHoldEdits] = useState({});
  const getHoldEdit = (work) =>
    holdEdits[work.id] || { reason: "", completionDate: "" };
  const setHoldEditField = (work, key, value) =>
    setHoldEdits((prev) => ({
      ...prev,
      [work.id]: { ...getHoldEdit(work), [key]: value },
    }));

  const handleHoldWork = async ({ mainId, subId, work }) => {
    if (!selectedReport?._id) return;
    const edit = getHoldEdit(work);
    const reason = String(edit.reason || "").trim();
    if (!reason) {
      Swal.fire({ icon: "warning", title: "Reason required", text: "Enter why this work is being put on hold." });
      return;
    }
    try {
      setSaving(true);
      await holdStaffReportWorkApi(selectedReport._id, {
        mainId,
        subId: subId || undefined,
        reason,
      });
      dispatch(appApi.util.invalidateTags(["WorkProgressReports"]));
      setHoldEdits((prev) => {
        const next = { ...prev };
        delete next[work.id];
        return next;
      });
      Swal.fire({
        icon: "success",
        title: "On hold",
        text: "This work is paused — its deadline countdown is frozen.",
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to hold",
        text: error?.response?.data?.message || "Could not put this work on hold.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleResumeWork = async ({ mainId, subId, work }) => {
    if (!selectedReport?._id) return;
    const edit = getHoldEdit(work);
    const completionDate = String(edit.completionDate || "").trim();
    if (!completionDate) {
      Swal.fire({ icon: "warning", title: "New completion date required", text: "Set the new target date to resume this work." });
      return;
    }
    try {
      setSaving(true);
      await resumeStaffReportWorkApi(selectedReport._id, {
        mainId,
        subId: subId || undefined,
        completionDate,
      });
      dispatch(appApi.util.invalidateTags(["WorkProgressReports"]));
      setHoldEdits((prev) => {
        const next = { ...prev };
        delete next[work.id];
        return next;
      });
      Swal.fire({
        icon: "success",
        title: "Resumed",
        text: "Counting down to the new completion date.",
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to resume",
        text: error?.response?.data?.message || "Could not resume this work.",
      });
    } finally {
      setSaving(false);
    }
  };

  // Self-service hold/resume panel — shown for every main-work-with-no-subs
  // and every sub-work (see renderSheetBreakdown), regardless of who's
  // viewing; the backend enforces that only the report's own employee or an
  // admin can actually submit.
  const renderHoldPanel = ({ work, mainId, subId }) => {
    const workKind = subId ? "Sub-work" : "Main work";
    const onHold = isWorkOnHold(work);
    const edit = getHoldEdit(work);
    const history = Array.isArray(work.holdHistory) ? work.holdHistory : [];
    if (onHold) {
      return (
        <div className="mt-3 rounded-xl border border-blue-200 bg-blue-50/40 p-3">
          <div className="mb-1 flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-700">
            <i className="fa-solid fa-pause" />
            {workKind} on hold since {formatDateLabel(work.holdDate)}
          </div>
          {work.holdReason && (
            <p className="mb-2 text-sm text-gray-700">“{work.holdReason}”</p>
          )}
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-44">
              <label className={labelClass}>New Completion Date</label>
              <input
                type="date"
                className={inputClass}
                autoComplete="off"
                value={edit.completionDate || ""}
                onChange={(e) => setHoldEditField(work, "completionDate", e.target.value)}
                disabled={selectedReport.isComplete}
              />
            </div>
            <button
              type="button"
              className="rounded-full bg-blue-600 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-white transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
              onClick={() => handleResumeWork({ mainId, subId, work })}
              disabled={saving || selectedReport.isComplete}
            >
              {saving ? "Saving..." : "Resume"}
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
        <div className="mb-2 flex flex-wrap items-end gap-3">
          <div className="min-w-[240px] flex-1">
            <label className={labelClass}>Hold Reason</label>
            <input
              className={inputClass}
              autoComplete="off"
              value={edit.reason || ""}
              onChange={(e) => setHoldEditField(work, "reason", e.target.value)}
              placeholder="Why is this work being paused?"
              disabled={selectedReport.isComplete}
            />
          </div>
          <button
            type="button"
            className="rounded-full border-2 border-blue-400 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-700 transition hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
            onClick={() => handleHoldWork({ mainId, subId, work })}
            disabled={saving || selectedReport.isComplete}
          >
            <i className="fa-solid fa-pause mr-1.5" />
            Hold
          </button>
        </div>
        {history.length > 0 && (
          <div className="space-y-1 border-t border-gray-200 pt-2">
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-500">
              Hold history ({history.length})
            </div>
            {history.map((h, index) => (
              <div key={index} className="text-[11px] text-gray-600">
                {formatDateLabel(h.holdDate)}
                {h.resumeDate ? ` → ${formatDateLabel(h.resumeDate)}` : " (open)"}
                {h.reason ? ` — ${h.reason}` : ""}
                {h.newCompletionDate
                  ? ` · new deadline ${formatDateLabel(h.newCompletionDate)}`
                  : ""}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ── Admin-only hard delete of a wrongly-created main / sub work ────────────
  // Removes the row from the sheet for good (with every sub-work when a main
  // work is deleted). Admin only, and always behind a typed confirmation.
  // Super-admin: hand this whole Field Execution Journal sheet (unit + all
  // its works) to another Project Manager / Civil Engineer / Supervisor /
  // Foreman. History, holds, extensions and verifications move with it.
  const handleTransferWork = async () => {
    if (!isSuperAdmin || !selectedReport?._id) return;
    let staff = [];
    try {
      const { data } = await getFieldEmployeesApi();
      staff = (Array.isArray(data?.staff) ? data.staff : []).filter(
        (person) => String(person._id) !== String(selectedReport.employeeId),
      );
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to load employees",
        text: error?.response?.data?.message || "Please try again.",
      });
      return;
    }
    if (!staff.length) {
      Swal.fire({
        icon: "info",
        title: "No one to transfer to",
        text: "There is no other active Project Manager, Civil Engineer, Supervisor or Foreman.",
      });
      return;
    }
    const inputOptions = {};
    staff.forEach((person) => {
      const group = person.role || "Other";
      inputOptions[group] = inputOptions[group] || {};
      inputOptions[group][person._id] = person.name;
    });
    const esc = (text) =>
      String(text || "").replace(/[&<>"']/g, (ch) => `&#${ch.charCodeAt(0)};`);
    const fromLabel = `${selectedReport.employeeName || "current employee"}${
      selectedReport.employeeRole ? ` (${selectedReport.employeeRole})` : ""
    }`;
    const { isConfirmed, value: employeeId } = await Swal.fire({
      title: "Transfer work",
      html: `Move this whole sheet${
        selectedReport.formData?.unitName
          ? ` for <b>${esc(selectedReport.formData.unitName)}</b>`
          : ""
      } from <b>${esc(fromLabel)}</b> to:`,
      input: "select",
      inputOptions,
      inputPlaceholder: "Select employee",
      showCancelButton: true,
      confirmButtonText: "Transfer",
      confirmButtonColor: "#f59e0b",
      inputValidator: (value) => (!value ? "Select an employee." : undefined),
    });
    if (!isConfirmed || !employeeId) return;
    try {
      setSaving(true);
      const { data } = await transferStaffReportApi(selectedReport._id, {
        employeeId,
      });
      dispatch(appApi.util.invalidateTags(["WorkProgressReports"]));
      Swal.fire({
        icon: "success",
        title: "Transferred",
        text: `Work is now assigned to ${data?.report?.employeeName || "the selected employee"}.`,
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to transfer",
        text: error?.response?.data?.message || "Could not transfer the work.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteWork = async ({ mainId, subId, work }) => {
    if (!isSuperAdmin || !selectedReport?._id) return;
    const isMain = !subId;
    const name =
      (isMain ? work?.mainWorkName : work?.subWorkName) ||
      (isMain ? "this main work" : "this sub-work");
    const { isConfirmed } = await Swal.fire({
      icon: "warning",
      title: isMain ? "Delete this main work?" : "Delete this sub-work?",
      html: isMain
        ? `<b>${name}</b> and <b>all of its sub-works</b> will be permanently removed from this sheet. This cannot be undone.`
        : `<b>${name}</b> will be permanently removed from this sheet. This cannot be undone.`,
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#dc2626",
      focusCancel: true,
    });
    if (!isConfirmed) return;
    try {
      setSaving(true);
      await deleteStaffReportWorkApi(selectedReport._id, {
        mainId,
        subId: subId || undefined,
      });
      dispatch(appApi.util.invalidateTags(["WorkProgressReports"]));
      setDateEdits((prev) => {
        const next = { ...prev };
        delete next[work?.id];
        return next;
      });
      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: isMain ? "Main work removed." : "Sub-work removed.",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to delete",
        text:
          error?.response?.data?.message || "Could not delete the work row.",
      });
    } finally {
      setSaving(false);
    }
  };

  // Admin-only deadline editor: correct the original completion date, or push
  // the target out with an extension (the original is kept so "delayed by N
  // days" always measures from the first promised date).
  const renderWorkDatesPanel = ({ work, mainId, subId }) => {
    if (!canAdminWorks) return null;
    const edit = getDateEdit(work);
    const info = getWorkOverdueInfo(work);
    const extensionsLog = Array.isArray(work.extensions) ? work.extensions : [];
    const extensionCount = extensionsLog.length;
    const workKind = subId ? "Sub-work" : "Main work";
    const workName =
      (subId ? work.subWorkName : work.mainWorkName) ||
      `Untitled ${workKind.toLowerCase()}`;
    return (
      <div className="mt-3 rounded-xl border border-sky-200 bg-sky-50/40 p-3">
        <div className="mb-1 flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-700">
          <i className="fa-solid fa-calendar-day" />
          Deadline (admin)
          {extensionCount > 0 && (
            <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-bold text-sky-700">
              Extended {extensionCount}×
            </span>
          )}
        </div>
        <div className="mb-2 text-sm font-semibold text-gray-800">
          <span className="mr-1.5 rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-sky-700">
            {workKind}
          </span>
          {workName}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-44">
            <label className={labelClass}>Original Completion</label>
            <input
              type="date"
              className={inputClass}
              autoComplete="off"
              value={edit.completionDate || ""}
              onChange={(e) =>
                setDateEditField(work, "completionDate", e.target.value)
              }
              disabled={selectedReport.isComplete}
            />
          </div>
          <div className="w-44">
            <label className={labelClass}>Extend To</label>
            <input
              type="date"
              className={inputClass}
              autoComplete="off"
              min={edit.completionDate || undefined}
              value={edit.extendedDate || ""}
              onChange={(e) =>
                setDateEditField(work, "extendedDate", e.target.value)
              }
              disabled={selectedReport.isComplete}
            />
          </div>
          {edit.extendedDate && (
            <button
              type="button"
              className="rounded-full border-2 border-sky-400 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-700 transition hover:bg-sky-100 disabled:opacity-60"
              onClick={() => setDateEditField(work, "extendedDate", "")}
              disabled={selectedReport.isComplete}
            >
              Clear extension
            </button>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <div className="min-w-[240px] flex-1">
            <label className={labelClass}>Reason for extension</label>
            <input
              className={inputClass}
              autoComplete="off"
              value={edit.reason || ""}
              onChange={(e) =>
                setDateEditField(work, "reason", e.target.value)
              }
              placeholder="Why is this deadline being extended?"
              disabled={selectedReport.isComplete || !edit.extendedDate}
            />
          </div>
          <label className="flex items-center gap-2 pb-2 text-sm font-medium text-gray-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
              checked={Boolean(edit.dueToEmployee)}
              onChange={(e) =>
                setDateEditField(work, "dueToEmployee", e.target.checked)
              }
              disabled={selectedReport.isComplete || !edit.extendedDate}
            />
            Extended due to the employee
          </label>
          <button
            type="button"
            className="rounded-full bg-sky-600 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-white transition hover:bg-sky-500 disabled:cursor-not-allowed disabled:opacity-60"
            onClick={() => handleSaveWorkDates({ mainId, subId, work })}
            disabled={saving || selectedReport.isComplete}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
        {info && (
          <p className="mt-2 text-xs text-sky-800">
            Original {formatDateLabel(info.originalDeadline)}
            {info.extended
              ? ` → extended to ${formatDateLabel(info.extendedDate)} (+${
                  info.extensionDays
                } day${info.extensionDays === 1 ? "" : "s"})`
              : ""}
            {info.delayDays > 0
              ? ` · delayed by ${info.delayDays} day${
                  info.delayDays === 1 ? "" : "s"
                }`
              : " · on schedule"}
          </p>
        )}
        {extensionCount > 0 && (
          <div className="mt-2 space-y-1 border-t border-sky-200 pt-2">
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-sky-700">
              Extension history ({extensionCount})
            </div>
            {extensionsLog.map((ext, index) => (
              <div key={index} className="text-[11px] text-sky-800">
                <span className="font-semibold">
                  {formatDateLabel(ext.date)}
                </span>
                {ext.dueToEmployee && (
                  <span className="ml-1 rounded-full bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold text-rose-700">
                    Employee
                  </span>
                )}
                {ext.reason ? ` — ${ext.reason}` : ""}
                {ext.extendedByName ? ` · by ${ext.extendedByName}` : ""}
                {ext.extendedAt
                  ? ` · ${formatDateLabel(ext.extendedAt)}`
                  : ""}
              </div>
            ))}
          </div>
        )}
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-sky-200 pt-3">
          <span className="text-[11px] text-rose-700">
            {subId
              ? "Created this sub-work by mistake? Remove it from the sheet."
              : "Created this main work by mistake? Remove it and its sub-works."}
          </span>
          <button
            type="button"
            className="rounded-full border-2 border-rose-400 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-rose-700 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
            onClick={() => handleDeleteWork({ mainId, subId, work })}
            disabled={saving}
          >
            <i className="fa-solid fa-trash-can mr-1.5" />
            {subId ? "Delete sub-work" : "Delete main work"}
          </button>
        </div>
      </div>
    );
  };

  // Admin-only sign-off block, shown under a completed main/sub work: a
  // "Verified" checkbox + a remarks field, submitted together.
  const renderVerificationPanel = ({ work, mainId, subId }) => {
    if (!canVerifyWorks || !isWorkComplete(work.status)) return null;
    const edit = getVerifyEdit(work);
    const workKind = subId ? "Sub-work" : "Main work";
    const workName =
      (subId ? work.subWorkName : work.mainWorkName) ||
      `Untitled ${workKind.toLowerCase()}`;
    return (
      <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50/40 p-3">
        <div className="mb-1 flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700">
          <i className="fa-solid fa-user-shield" />
          Admin Verification
          <VerifiedBadge work={work} />
          <WellDoneBadge work={work} />
        </div>
        <div className="mb-2 text-sm font-semibold text-gray-800">
          <span className="mr-1.5 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700">
            {workKind}
          </span>
          {workName}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
              checked={Boolean(edit.verified)}
              onChange={(e) =>
                setVerifyEditField(work, "verified", e.target.checked)
              }
            />
            Mark this completed work as verified
          </label>
          <div className="min-w-[220px] flex-1">
            <label className={labelClass}>Verification Remarks</label>
            <input
              className={inputClass}
              autoComplete="off"
              value={edit.remarks || ""}
              onChange={(e) =>
                setVerifyEditField(work, "remarks", e.target.value)
              }
              placeholder="Site checked, quality approved…"
            />
          </div>
          <div className="min-w-[200px]">
            <label className={labelClass}>Site Photo</label>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              className="block w-full text-xs text-gray-600"
              onChange={(e) =>
                setVerifyEditField(work, "photo", e.target.files?.[0] || null)
              }
            />
          </div>
          <button
            type="button"
            className="rounded-full bg-emerald-600 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-white transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-60"
            onClick={() => handleSubmitVerification({ mainId, subId, work })}
            disabled={saving}
          >
            {saving ? "Saving..." : "Submit"}
          </button>
        </div>
        {isWorkVerified(work) && (
          <p className="mt-2 text-xs text-emerald-700">
            Verified
            {work.verification.verifiedByName
              ? ` by ${work.verification.verifiedByName}`
              : ""}
            {work.verification.verifiedAt
              ? ` on ${formatDateLabel(work.verification.verifiedAt)}`
              : ""}
            {work.verification.remarks
              ? ` — “${work.verification.remarks}”`
              : ""}
            {work.verification.photoUrl && (
              <>
                {" · "}
                <a
                  href={resolveUrl(work.verification.photoUrl)}
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold underline"
                >
                  Site photo
                </a>
              </>
            )}
          </p>
        )}
      </div>
    );
  };

  // Compact remarks card for a main/sub work: shows only the CURRENT (latest)
  // dated update, with a "View all" button that opens the full date-wise
  // history in a modal — instead of dumping every entry inline.
  const renderRemarksPreview = (work, title, { mainId, subId } = {}) => {
    const history = sortSubWorkUpdates(work.updatedRemarks, {
      newestFirst: true,
    });
    if (!history.length) return null;
    const latest = history[0];
    const editable = canEditWorkUpdates && Boolean(mainId);
    return (
      <div className="mt-3 border-t border-gray-200 pt-2">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="text-[11px] text-gray-600">
            <span className="font-semibold text-gray-700">
              {formatDateLabel(latest.date)}
            </span>
            {latest.peopleCount !== "" && latest.peopleCount != null
              ? ` · 👥 ${latest.peopleCount}`
              : ""}
            {latest.text ? ` — ${latest.text}` : ""}
          </div>
          {(history.length > 1 || editable) && (
            <button
              type="button"
              className="whitespace-nowrap text-[11px] font-semibold uppercase tracking-[0.14em] text-amber-700 hover:text-amber-800"
              onClick={() =>
                setRemarksModal({
                  title,
                  history,
                  mainId: editable ? mainId : undefined,
                  subId,
                })
              }
            >
              {editable ? "View / Edit" : "View all"} ({history.length})
            </button>
          )}
        </div>
      </div>
    );
  };

  // The work whose updates the remarks modal shows, read from the live report
  // so a correction made in the modal shows up straight away.
  const remarksModalHistory = (() => {
    if (!remarksModal) return [];
    if (!remarksModal.mainId) return remarksModal.history;
    const main = (fieldReport.mainWorks || []).find(
      (mw) => String(mw.id) === String(remarksModal.mainId),
    );
    const work = remarksModal.subId
      ? (main?.subWorks || []).find(
          (sw) => String(sw.id) === String(remarksModal.subId),
        )
      : main;
    return sortSubWorkUpdates(work?.updatedRemarks, { newestFirst: true });
  })();

  // Clear "Edit updates" button for a main / sub work that has daily updates —
  // opens the remarks modal with Edit / Delete on every entry.
  const renderEditUpdatesButton = (work, title, { mainId, subId } = {}) => {
    const count = normalizeSubWorkUpdates(work?.updatedRemarks).length;
    if (!canEditWorkUpdates || !mainId || !count) return null;
    return (
      <button
        type="button"
        className="inline-flex items-center gap-1 rounded-full border-2 border-amber-500 px-3 py-0.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-amber-700 hover:bg-amber-50"
        onClick={() => setRemarksModal({ title, history: [], mainId, subId })}
      >
        <i className="fa-solid fa-pen-to-square" />
        Edit updates ({count})
      </button>
    );
  };

  // Correct or delete one wrongly-added daily update. The server matches the
  // entry by its original date + people + text.
  const handleSaveWorkUpdate = async (entry, { remove = false } = {}) => {
    if (!selectedReport?._id || !remarksModal?.mainId) return;
    if (remove) {
      const confirm = await Swal.fire({
        icon: "warning",
        title: "Delete this update?",
        text: `${formatDateLabel(entry.date)} — ${entry.text || "people count only"}`,
        showCancelButton: true,
        confirmButtonText: "Delete",
      });
      if (!confirm.isConfirmed) return;
    } else if (
      !String(updateEdit?.text || "").trim() &&
      String(updateEdit?.people ?? "").trim() === ""
    ) {
      Swal.fire({
        icon: "warning",
        title: "Nothing to save",
        text: "Enter a note or a people count, or delete the update instead.",
      });
      return;
    }
    try {
      setSaving(true);
      await editStaffReportWorkUpdateApi(selectedReport._id, {
        mainId: remarksModal.mainId,
        subId: remarksModal.subId || undefined,
        original: {
          date: entry.date,
          text: entry.text,
          peopleCount: entry.peopleCount,
        },
        text: remove ? entry.text : String(updateEdit?.text || "").trim(),
        peopleCount: remove
          ? entry.peopleCount
          : String(updateEdit?.people ?? "").trim(),
        date: remove ? undefined : updateEdit?.date || undefined,
        remove,
      });
      dispatch(appApi.util.invalidateTags(["WorkProgressReports"]));
      setUpdateEdit(null);
      Swal.fire({
        icon: "success",
        title: remove ? "Update deleted" : "Update corrected",
        timer: 1400,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to save",
        text:
          error?.response?.data?.message || "Could not change this update.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAddOtherWork = () => {
    setOtherWorks((prev) => [...prev, createOtherWork()]);
  };

  const handleUpdateOtherWork = (index, field, value) => {
    setOtherWorks((prev) =>
      prev.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry)),
    );
  };

  const handleRemoveOtherWork = (index) => {
    setOtherWorks((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : [createOtherWork()];
    });
  };

  const handleSaveTask = async () => {
    if (!selectedReport?._id) return;
    if (selectedReport.isComplete) {
      Swal.fire({
        icon: "info",
        title: "Read only",
        text: "Completed sheets cannot be edited.",
      });
      return;
    }

    const tasks = Array.isArray(selectedReport.formData?.tasks)
      ? selectedReport.formData.tasks
      : [];
    if (!tasks.length) return;

    const formType = Number(selectedReport.formType);
    const noteDate = taskEdits.date || toDateInputValue();
    const targetingSubWork = formType === 6 && selectedSubWorkIndex !== "";

    const updatedTasks = tasks.map((task, index) => {
      if (index !== selectedTaskIndex) return task;

      if (targetingSubWork) {
        // Same append-only pattern as a task's updatedRemarks, but scoped to
        // the one sub-work picked in the quick editor — the task itself
        // (and its own status/history) is left untouched.
        const subIndex = Number(selectedSubWorkIndex);
        const subWorks = Array.isArray(task.subWorks) ? task.subWorks : [];
        const noteText = taskEdits.remarks.trim();
        const updatedSubWorks = subWorks.map((subWork, subWorkIndex) => {
          if (subWorkIndex !== subIndex) return subWork;
          const history = normalizeRemarksHistory(subWork.updatedRemarks);
          return {
            ...subWork,
            status: taskEdits.status,
            updatedRemarks: noteText
              ? [...history, { date: noteDate, text: noteText }]
              : history,
          };
        });
        return { ...task, subWorks: updatedSubWorks };
      }

      if (formType === 6) {
        // Append a dated entry instead of overwriting — the original
        // `remarks` note stays untouched, follow-up notes stack up here.
        const history = normalizeRemarksHistory(task.updatedRemarks);
        const noteText = taskEdits.remarks.trim();
        return {
          ...task,
          status: taskEdits.status,
          updatedRemarks: noteText
            ? [...history, { date: noteDate, text: noteText }]
            : history,
        };
      }
      return { ...task, status: taskEdits.status, remarks: taskEdits.remarks };
    });

    try {
      setSaving(true);
      const payload = {
        tasks: updatedTasks,
        reportDate: selectedReport.reportDate,
        date: selectedReport.reportDate,
        status: selectedReport.status || "Pending",
        remarks: selectedReport.formData?.remarks || "",
        ...(formType === 6
          ? {
              otherWorks: otherWorks
                .map((entry) => ({ date: entry.date, text: entry.text.trim() }))
                .filter((entry) => entry.text),
            }
          : {}),
      };

      await updateStaffReportApi(selectedReport._id, payload);
      dispatch(appApi.util.invalidateTags(["WorkProgressReports"]));
      Swal.fire({
        icon: "success",
        title: "Saved",
        text: "The task has been updated.",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to save",
        text: error?.response?.data?.message || "Could not update the task.",
      });
    } finally {
      setSaving(false);
    }
  };

  // ── Role: Revenue (formType 6) — per-work card editor ─────────────────────
  // Every task / nested sub-work is edited from its own card (Status · Date ·
  // Add Remark · Update). No people. Once set to Complete the card locks and
  // sinks to the bottom of its list. "Other Work" is a separate dated list he
  // adds to daily.
  const [revEdits, setRevEdits] = useState({});
  const revKey = (ti, si) => (si == null ? `t${ti}` : `t${ti}s${si}`);
  const getRevEdit = (key, work) =>
    revEdits[key] || {
      status: work?.status || "Pending",
      date: toDateInputValue(),
      remark: "",
    };
  const setRevEditField = (key, field, value) =>
    setRevEdits((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [field]: value },
    }));

  const commitRevenueTasks = async (updatedTasks, successText) => {
    const payload = {
      tasks: updatedTasks,
      reportDate: selectedReport.reportDate,
      date: selectedReport.reportDate,
      status: selectedReport.status || "Pending",
      remarks: selectedReport.formData?.remarks || "",
      otherWorks: otherWorks
        .map((entry) => ({ date: entry.date, text: entry.text.trim() }))
        .filter((entry) => entry.text),
    };
    await updateStaffReportApi(selectedReport._id, payload);
    dispatch(appApi.util.invalidateTags(["WorkProgressReports"]));
    Swal.fire({
      icon: "success",
      title: "Saved",
      text: successText,
      timer: 1300,
      showConfirmButton: false,
    });
  };

  // Save one Revenue card. subIndex null = the whole task; otherwise the
  // nested sub-work at that index.
  const handleSaveRevenueCard = async (taskIndex, subIndex) => {
    if (!selectedReport?._id || selectedReport.isComplete) return;
    const tasks = Array.isArray(selectedReport.formData?.tasks)
      ? selectedReport.formData.tasks
      : [];
    if (!tasks[taskIndex]) return;
    const key = revKey(taskIndex, subIndex);
    const src =
      subIndex == null
        ? tasks[taskIndex]
        : (tasks[taskIndex].subWorks || [])[subIndex];
    const edit = getRevEdit(key, src);
    const noteDate = edit.date || toDateInputValue();
    const noteText = String(edit.remark || "").trim();

    const updatedTasks = tasks.map((task, ti) => {
      if (ti !== taskIndex) return task;
      if (subIndex == null) {
        const history = normalizeRemarksHistory(task.updatedRemarks);
        return {
          ...task,
          status: edit.status,
          updatedRemarks: noteText
            ? [...history, { date: noteDate, text: noteText }]
            : history,
        };
      }
      const subWorks = (task.subWorks || []).map((sub, si) => {
        if (si !== subIndex) return sub;
        const history = normalizeRemarksHistory(sub.updatedRemarks);
        return {
          ...sub,
          status: edit.status,
          updatedRemarks: noteText
            ? [...history, { date: noteDate, text: noteText }]
            : history,
        };
      });
      // Roll the task status up from its sub-works.
      let taskStatus = task.status;
      if (subWorks.length) {
        if (subWorks.every((s) => /complete/i.test(String(s.status || ""))))
          taskStatus = "Completed";
        else if (
          subWorks.some((s) => /complete|progress/i.test(String(s.status || "")))
        )
          taskStatus = "In Progress";
        else taskStatus = "Pending";
      }
      return { ...task, status: taskStatus, subWorks };
    });

    try {
      setSaving(true);
      await commitRevenueTasks(updatedTasks, "The work has been updated.");
      setRevEdits((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to save",
        text: error?.response?.data?.message || "Could not update the work.",
      });
    } finally {
      setSaving(false);
    }
  };

  // Add one "Other Work" line (date + text) and persist immediately.
  const [otherWorkDraft, setOtherWorkDraft] = useState({
    date: toDateInputValue(),
    text: "",
  });
  const handleAddRevenueOtherWork = async () => {
    const text = String(otherWorkDraft.text || "").trim();
    if (!text || !selectedReport?._id || selectedReport.isComplete) return;
    const tasks = Array.isArray(selectedReport.formData?.tasks)
      ? selectedReport.formData.tasks
      : [];
    const nextOther = [
      ...otherWorks
        .map((e) => ({ date: e.date, text: e.text.trim() }))
        .filter((e) => e.text),
      { date: otherWorkDraft.date || toDateInputValue(), text },
    ];
    try {
      setSaving(true);
      await updateStaffReportApi(selectedReport._id, {
        tasks,
        reportDate: selectedReport.reportDate,
        date: selectedReport.reportDate,
        status: selectedReport.status || "Pending",
        remarks: selectedReport.formData?.remarks || "",
        otherWorks: nextOther,
      });
      dispatch(appApi.util.invalidateTags(["WorkProgressReports"]));
      setOtherWorks(nextOther);
      setOtherWorkDraft({ date: toDateInputValue(), text: "" });
      Swal.fire({
        icon: "success",
        title: "Added",
        text: "Other work recorded.",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to save",
        text: error?.response?.data?.message || "Could not add the other work.",
      });
    } finally {
      setSaving(false);
    }
  };

  const renderRevenueSheetBreakdown = () => {
    const tasks = Array.isArray(selectedReport?.formData?.tasks)
      ? selectedReport.formData.tasks
      : [];
    const locked = Boolean(selectedReport?.isComplete);
    const isDone = (s) => /complete|completed|done/i.test(String(s || ""));
    const statusChip = (s) =>
      isDone(s)
        ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
        : /progress/i.test(String(s || ""))
          ? "bg-sky-50 text-sky-700 ring-1 ring-sky-200"
          : "bg-gray-100 text-gray-600 ring-1 ring-gray-200";

    // Plain function (NOT a component) — called inline so the <input>s keep
    // focus across re-renders instead of remounting on every keystroke.
    const renderCard = ({ rowKey, work, name, taskIndex, subIndex }) => {
      const key = revKey(taskIndex, subIndex);
      const done = isDone(work.status);
      const disabled = locked || done;
      const edit = getRevEdit(key, work);
      const history = [...normalizeRemarksHistory(work.updatedRemarks)].sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
      );
      return (
        <div
          key={rowKey}
          className={`rounded-xl border p-3 ${
            done
              ? "border-emerald-200 bg-emerald-50/60 opacity-80"
              : "border-gray-200 bg-white"
          }`}
        >
          <div className="mb-2 flex flex-wrap items-center gap-2 text-sm font-semibold text-gray-800">
            {name}
            <span
              className={`${statusChip(work.status)} rounded-full px-2.5 py-0.5 text-[11px] font-semibold`}
            >
              {work.status || "Pending"}
            </span>
            {done && (
              <span className="text-[11px] font-medium text-emerald-600">
                Locked
              </span>
            )}
          </div>
          {/* Super-admin sees updates only — no editing (the Revenue employee
              owns the report). */}
          {!isReviewer && (
            <div className="flex flex-wrap items-end gap-2">
              <div className="w-40">
                <label className={labelClass}>Status</label>
                <select
                  className={inputClass}
                  autoComplete="off"
                  value={edit.status || work.status || "Pending"}
                  onChange={(e) =>
                    setRevEditField(key, "status", e.target.value)
                  }
                  disabled={disabled}
                >
                  <option>Pending</option>
                  <option>In Progress</option>
                  <option>Complete</option>
                </select>
              </div>
              <div className="w-40">
                <label className={labelClass}>Date</label>
                <input
                  type="date"
                  className={inputClass}
                  autoComplete="off"
                  value={edit.date || toDateInputValue()}
                  onChange={(e) => setRevEditField(key, "date", e.target.value)}
                  disabled={disabled}
                />
              </div>
              <div className="min-w-[220px] flex-1">
                <label className={labelClass}>Add Remark</label>
                <input
                  className={inputClass}
                  autoComplete="off"
                  value={edit.remark || ""}
                  onChange={(e) =>
                    setRevEditField(key, "remark", e.target.value)
                  }
                  placeholder="New dated note — added, not replaced"
                  disabled={disabled}
                />
              </div>
              <button
                type="button"
                className="rounded-full bg-amber-500 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={() => handleSaveRevenueCard(taskIndex, subIndex)}
                disabled={saving || disabled}
              >
                {saving ? "Saving..." : done ? "Done" : "Update"}
              </button>
            </div>
          )}
          {history.length > 0 && (
            <div
              className={`space-y-1 ${
                isReviewer ? "" : "mt-3 border-t border-gray-200 pt-2"
              }`}
            >
              {history.map((entry, i) => (
                <div key={i} className="text-[11px] text-gray-600">
                  <span className="font-semibold text-gray-700">
                    {formatDateLabel(entry.date)}
                  </span>
                  {entry.text ? ` — ${entry.text}` : ""}
                </div>
              ))}
            </div>
          )}
          {isReviewer && history.length === 0 && (
            <div className="text-[11px] text-gray-400">No updates yet.</div>
          )}
        </div>
      );
    };

    const orderedTasks = tasks
      .map((task, index) => ({ task, index }))
      .sort(
        (a, b) => Number(isDone(a.task.status)) - Number(isDone(b.task.status)),
      );

    const otherList = normalizeOtherWorks(selectedReport?.formData?.otherWorks)
      .filter((e) => e.text.trim())
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    return (
      <div className="space-y-5">
        {orderedTasks.length === 0 && (
          <div className="rounded-xl border border-dashed border-gray-300 p-4 text-center text-sm text-gray-500">
            No work added yet.
          </div>
        )}
        {orderedTasks.map(({ task, index }) => {
          const subs = Array.isArray(task.subWorks) ? task.subWorks : [];
          const orderedSubs = subs
            .map((sub, si) => ({ sub, si }))
            .sort(
              (a, b) =>
                Number(isDone(a.sub.status)) - Number(isDone(b.sub.status)),
            );
          return (
            <div key={index} className="space-y-2">
              <div className="flex flex-wrap items-center gap-2 text-lg font-semibold text-gray-700">
                {task.work || `Work ${index + 1}`}
                <span
                  className={`${statusChip(task.status)} rounded-full px-2.5 py-0.5 text-xs font-semibold`}
                >
                  {task.status || "Pending"}
                </span>
              </div>
              {orderedSubs.length === 0
                ? renderCard({
                    rowKey: "main",
                    work: task,
                    name: task.work || `Work ${index + 1}`,
                    taskIndex: index,
                    subIndex: null,
                  })
                : (
                  <div className="space-y-2">
                    {orderedSubs.map(({ sub, si }) =>
                      renderCard({
                        rowKey: si,
                        work: sub,
                        name: sub.subWorkName || `Sub-work ${si + 1}`,
                        taskIndex: index,
                        subIndex: si,
                      }),
                    )}
                  </div>
                )}
            </div>
          );
        })}

        {/* Other Work — free daily entries, not tied to the task table */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
            Other Work
          </h4>
          {!locked && !isReviewer && (
            <div className="flex flex-wrap items-end gap-2 rounded-xl border border-gray-200 bg-white p-3">
              <div className="w-40">
                <label className={labelClass}>Date</label>
                <input
                  type="date"
                  className={inputClass}
                  autoComplete="off"
                  value={otherWorkDraft.date}
                  onChange={(e) =>
                    setOtherWorkDraft((d) => ({ ...d, date: e.target.value }))
                  }
                />
              </div>
              <div className="min-w-[220px] flex-1">
                <label className={labelClass}>Other work item</label>
                <input
                  className={inputClass}
                  autoComplete="off"
                  value={otherWorkDraft.text}
                  onChange={(e) =>
                    setOtherWorkDraft((d) => ({ ...d, text: e.target.value }))
                  }
                  placeholder="What else was done today"
                />
              </div>
              <button
                type="button"
                className="rounded-full bg-amber-500 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={handleAddRevenueOtherWork}
                disabled={saving || !otherWorkDraft.text.trim()}
              >
                Add
              </button>
            </div>
          )}
          {otherList.length ? (
            <ul className="list-disc space-y-1 rounded-xl border border-gray-200 bg-white px-8 py-3 text-sm text-gray-700">
              {otherList.map((entry, i) => (
                <li key={i}>
                  <span className="font-medium text-gray-500">
                    {formatDateLabel(entry.date)}:
                  </span>{" "}
                  {entry.text}
                </li>
              ))}
            </ul>
          ) : (
            <div className="rounded-xl border border-dashed border-gray-300 p-4 text-center text-sm text-gray-500">
              No other work recorded yet.
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleSave = async () => {
    if (!selectedReport?._id) return;
    if (selectedReport.isComplete) {
      Swal.fire({
        icon: "info",
        title: "Read only",
        text: "Completed sheets cannot be edited.",
      });
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...editForm,
        reportDate: selectedReport.reportDate,
        date: selectedReport.reportDate,
        status: editForm.status || selectedReport.status || "Pending",
        remarks: editForm.remarks || "",
      };

      await updateStaffReportApi(selectedReport._id, payload);
      dispatch(appApi.util.invalidateTags(["WorkProgressReports"]));
      Swal.fire({
        icon: "success",
        title: "Saved",
        text: "The report has been updated.",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to save",
        text: error?.response?.data?.message || "Could not update the report.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleExportExcel = async () => {
    if (!selectedReport) return;
    try {
      setExporting(true);
      await exportStaffReportToExcel({
        report: selectedReport,
        employeeName,
        employeeRole,
        technicalStaffById,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to export",
        text: "Something went wrong while generating the Excel sheet.",
      });
    } finally {
      setExporting(false);
    }
  };

  const formatStatusClass = (report) => {
    if (report?.isComplete)
      return "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200";
    if (/review/i.test(String(report?.status || "")))
      return "bg-amber-50 text-amber-700 ring-1 ring-amber-200";
    return "bg-sky-50 text-sky-700 ring-1 ring-sky-200";
  };

  // Generic spreadsheet-style table — used for every form's breakdown so the
  // layout reads like the source Excel sheet (columns + rows), not cards.
  const renderTable = (columns, rows) => {
    const list = Array.isArray(rows) ? rows : [];
    if (!list.length) {
      return (
        <div className="rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
          No entries recorded yet.
        </div>
      );
    }
    // Tables with a `scrollable` column (currently just the Revenue sheet's
    // Remarks/Updated Remarks) use a fixed layout so those columns can grow
    // long and scroll internally without widening the whole table and
    // pushing every other field off-screen. Tables without one keep the
    // original single-line/whole-table-scrolls layout unchanged.
    const hasScrollableColumn = columns.some((col) => col.scrollable);
    return (
      <div
        className={`rounded-xl border border-gray-200 ${hasScrollableColumn ? "" : "overflow-x-auto"}`}
      >
        <table
          className={`${hasScrollableColumn ? "w-full table-fixed" : "min-w-full"} divide-y divide-gray-200 text-sm`}
        >
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="whitespace-nowrap px-3 py-1.5 text-left font-medium"
                  style={col.scrollable ? { width: 220 } : undefined}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {list.map((row, index) => (
              <tr key={index}>
                {columns.map((col) =>
                  col.scrollable ? (
                    // These columns can grow long (dated remarks history) — cap
                    // the cell's width and let it scroll on its own instead of
                    // widening the whole table and pushing other fields off-screen.
                    <td key={col.key} className="px-3 py-1.5" style={{ width: 220 }}>
                      <div
                        className="overflow-x-auto whitespace-nowrap text-gray-700"
                        style={{ maxWidth: 220 }}
                      >
                        {col.render ? col.render(row) : row[col.key] || "—"}
                      </div>
                    </td>
                  ) : (
                    <td
                      key={col.key}
                      className={`px-3 py-1.5 text-gray-700 ${hasScrollableColumn ? "whitespace-normal break-words" : "whitespace-nowrap"}`}
                    >
                      {col.render ? col.render(row) : row[col.key] || "—"}
                    </td>
                  ),
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderMetricsMatrix = (metrics) => {
    const list = Array.isArray(metrics) ? metrics : [];
    const dateSet = new Set();
    list.forEach((metric) => {
      (Array.isArray(metric?.entries) ? metric.entries : []).forEach(
        (entry) => {
          if (entry?.date) dateSet.add(entry.date);
        },
      );
    });
    const dates = Array.from(dateSet).sort().reverse();
    // A back-dated column: the server stamps each entry with when it was
    // really saved, so show that day under the column's date.
    const submittedOn = {};
    list.forEach((metric) =>
      (Array.isArray(metric?.entries) ? metric.entries : []).forEach((entry) => {
        if (!entry?.date || !entry.savedAt) return;
        const day = istDayKey(entry.savedAt);
        if (day > entry.date) {
          (submittedOn[entry.date] = submittedOn[entry.date] || new Set()).add(day);
        }
      }),
    );

    if (!dates.length) {
      return (
        <div className="rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
          No metric entries have been recorded yet.
        </div>
      );
    }

    return (
      <div
        className="w-full max-w-full overflow-x-auto rounded-xl border border-gray-200"
        style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-x" }}
      >
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              <th className="sticky left-0 whitespace-nowrap bg-gray-50 px-3 py-1.5 text-left font-medium">
                Metric
              </th>
              {dates.map((date) => (
                <th
                  key={date}
                  className="whitespace-nowrap px-3 py-1.5 text-left font-medium"
                >
                  {formatDateLabel(date)}
                  {submittedOn[date] && (
                    <div className="text-[11px] font-semibold text-amber-700">
                      Submitted on{" "}
                      {[...submittedOn[date]].sort().map(formatDateLabel).join(", ")}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {list.map((metric) => (
              <tr key={metric.key}>
                <td className="sticky left-0 whitespace-nowrap bg-white px-3 py-1.5 font-medium text-gray-700">
                  {metric.label}
                </td>
                {dates.map((date) => (
                  <td
                    key={date}
                    className="whitespace-nowrap px-3 py-1.5 text-gray-600"
                  >
                    {getMetricEntryValue(metric, date) || "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Tele Calling — the ORIGINAL fixed daily calling-metric grid (Netplus /
  // New Leads / Hotel / CP / Client calling …), one value per metric per day.
  const renderCallingMetrics = () => {
    const rows =
      Array.isArray(editForm.metrics) && editForm.metrics.length
        ? editForm.metrics
        : createMetricSet(3);
    const locked = selectedReport.isComplete;
    const today = toDateInputValue();
    return (
      <div className="mt-6 space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gray-500">
          Daily Calling Metrics
        </h3>
        <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
          <p className="mb-2 text-xs text-gray-400">
            Number fields <b>add</b> to that day's count on Save. Data
            Maintained / Extra Work are free-text remarks for the day. Pick an
            earlier Date to edit a past day.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-44">
              <label className={labelClass}>Date</label>
              <input
                type="date"
                className={inputClass}
                autoComplete="off"
                value={metricsDate}
                max={today}
                onChange={(e) => handleMetricsDateChange(e.target.value)}
                disabled={locked}
              />
            </div>
            {rows.map((metric) => {
              const isText = isTextMetricKey(3, metric.key);
              const saved = getMetricEntryValue(metric, metricsDate);
              return (
                <div
                  key={metric.key}
                  className={isText ? "min-w-[220px] flex-1" : "w-36"}
                >
                  <label className={labelClass}>{metric.label}</label>
                  <input
                    className={inputClass}
                    autoComplete="off"
                    {...(isText ? {} : { inputMode: "numeric" })}
                    placeholder={
                      isText
                        ? saved
                          ? `Saved: ${saved}`
                          : "Remark for this date"
                        : "0"
                    }
                    value={metricDraft[metric.key] ?? ""}
                    onChange={(e) =>
                      handleMetricField(metric.key, e.target.value)
                    }
                    disabled={locked}
                  />
                </div>
              );
            })}
            <button
              type="button"
              className="rounded-full bg-amber-500 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
              onClick={handleSaveMetrics}
              disabled={saving || locked}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Tele Calling (3) & Real Estate Sales (4) — summary tiles, plus (Sales) the
  // manual daily Open/Site Visits & Sales counter. The contact roster,
  // call log and remarks are managed from the Customer Database page.
  const renderReportingSummary = (formType) => {
    const isSales = formType === 4;
    const locked = selectedReport.isComplete;
    const today = toDateInputValue();
    const summary = deriveReportingSummary(editForm, today);
    const btnPrimary =
      "rounded-full bg-amber-500 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60";

    // A tile showing today's number big, with this-month + running below.
    const statTile = (label, s, note) => (
      <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3">
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400">
          {label} <span className="text-gray-300">(today)</span>
        </div>
        <div className="mt-1 text-2xl font-semibold text-gray-900">
          {s.today}
        </div>
        <div className="mt-0.5 text-[11px] text-gray-400">
          This month {s.month} · Running {s.running}
        </div>
        {note ? (
          <div className="text-[11px] text-gray-300">{note}</div>
        ) : null}
      </div>
    );

    const dailyFields = [
      ["openVisits", "Open Visits"],
      ["siteVisits", "Site Visits"],
      ["calls", "Phone Calls"],
      ["sales", "Sales (deals)"],
    ];

    return (
      <div className="mt-6 space-y-5">
        <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gray-500">
          Summary
        </h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {isSales && statTile("Open Visits", summary.openVisits)}
          {isSales && statTile("Site Visits", summary.siteVisits)}
          {statTile(
            "Calls",
            summary.calls,
            "manual daily + per-contact logged",
          )}
          {isSales && statTile("Sales", summary.sales, "deals · manual")}
          <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3">
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400">
              Contacts Logged
            </div>
            <div className="mt-1 text-2xl font-semibold text-gray-900">
              {summary.contacts}
            </div>
          </div>
        </div>

        {isSales && (
          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
            <h4 className={labelClass}>Daily Activity</h4>
            <p className="mb-2 text-xs text-gray-400">
              Enter what you did and press Save — each Save <b>adds</b> to that
              day's total, so you can log activity in batches. Pick an earlier
              Date to add to a past day. Month &amp; running totals above
              accumulate and carry forward across days and months.
            </p>
            <div className="flex flex-wrap items-end gap-3">
              <div className="w-44">
                <label className={labelClass}>Date</label>
                <input
                  type="date"
                  className={inputClass}
                  autoComplete="off"
                  value={statsDate}
                  max={today}
                  onChange={(e) => handleStatsDateChange(e.target.value)}
                  disabled={locked}
                />
              </div>
              {dailyFields.map(([key, label]) => (
                <div key={key} className="w-28">
                  <label className={labelClass}>{label}</label>
                  <input
                    className={inputClass}
                    autoComplete="off"
                    inputMode="text"
                    placeholder="Enter here"
                    value={dailyDraft[key] ?? ""}
                    onChange={(e) => handleDailyStatField(key, e.target.value)}
                    disabled={locked}
                  />
                </div>
              ))}
              <div className="min-w-[14rem] flex-1">
                <label className={labelClass}>Remarks</label>
                <input
                  className={inputClass}
                  autoComplete="off"
                  placeholder="Any remarks for the day"
                  value={dailyDraft.remarks ?? ""}
                  onChange={(e) => handleDailyStatField("remarks", e.target.value)}
                  disabled={locked}
                />
              </div>
              <button
                type="button"
                className={btnPrimary}
                onClick={handleSaveDailyStats}
                disabled={saving || locked}
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Read-only recap of a Tele Calling / Real Estate Sales sheet — contacts,
  // the flattened call log, and (Sales) the daily Visits/Sales rows.
  const renderContactBreakdown = (formData = {}, { withStats } = {}) => {
    const contacts = Array.isArray(formData.contacts) ? formData.contacts : [];
    // Latest date on top for both tables.
    const newestFirst = (field) => (a, b) =>
      String(b[field] || "").localeCompare(String(a[field] || ""));
    const dailyStats = (
      Array.isArray(formData.dailyStats) ? formData.dailyStats : []
    )
      .slice()
      .sort(newestFirst("date"));
    const sortedContacts = contacts.slice().sort(newestFirst("createdDate"));
    // Remarks/call history now lives on the Master Customer record
    // (dailyRemarks, managed from the Customer Database page) — merge in any
    // older per-report `calls` entries too, de-duped, so nothing is dropped.
    const contactHistory = (c) => {
      const history = Array.isArray(c.dailyRemarks) ? c.dailyRemarks : [];
      const calls = Array.isArray(c.calls) ? c.calls : [];
      const seen = new Set(history.map((h) => `${h.date}::${h.remarks}`));
      return [
        ...history.map((h) => ({ date: h.date, remarks: h.remarks })),
        ...calls
          .filter(
            (call) => !seen.has(`${call.date}::${(call.remarks || "").trim()}`),
          )
          .map((call) => ({ date: call.date, remarks: call.remarks })),
      ];
    };
    const callLog = contacts
      .flatMap((c) =>
        contactHistory(c).map((entry) => ({
          date: entry.date,
          customer: c.customerName || c.customerPhone || "—",
          remarks: entry.remarks || "—",
        })),
      )
      .sort((a, b) => String(b.date).localeCompare(String(a.date)));

    return (
      <div className="space-y-5">
        {withStats && (
          <div>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
              Daily Activity
            </h4>
            {renderTable(
              [
                {
                  key: "date",
                  label: "Date",
                  render: (r) => formatDateLabel(r.date),
                },
                {
                  key: "openVisits",
                  label: "Open Visits",
                  render: (r) => r.openVisits ?? r.visits ?? 0,
                },
                { key: "siteVisits", label: "Site Visits" },
                { key: "calls", label: "Phone Calls" },
                { key: "sales", label: "Sales (deals)" },
                { key: "remarks", label: "Remarks" },
              ],
              dailyStats,
            )}
          </div>
        )}
        <div>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
            Contacts
          </h4>
          {renderTable(
            [
              {
                key: "createdDate",
                label: "Added On",
                render: (r) => (r.createdDate ? formatDateLabel(r.createdDate) : "—"),
              },
              { key: "customerName", label: "Customer" },
              { key: "customerPhone", label: "Customer Phone" },
              { key: "remarks", label: "Remarks" },
              { key: "lead", label: "Lead" },
              { key: "propertyType", label: "Type" },
              { key: "budget", label: "Budget" },
              {
                key: "calls",
                label: "Calls",
                render: (r) => contactHistory(r).length,
              },
            ],
            sortedContacts,
          )}
        </div>
        <div>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
            Call Log
          </h4>
          {renderTable(
            [
              {
                key: "date",
                label: "Date",
                render: (r) => formatDateLabel(r.date),
              },
              { key: "customer", label: "Customer" },
              { key: "remarks", label: "Remark" },
            ],
            callLog,
          )}
        </div>
      </div>
    );
  };

  // ── Role: Revenue — Field Execution Journal (formType 1) variant ──────────
  // Card list like the Employee Reports view; every work / sub-work editable in
  // place (Status · Date · Add Remark · Update). No people field. Pending / In
  // Progress on top; once Complete the card locks and drops to the bottom.
  const renderRevenueFieldBreakdown = () => {
    const field = fieldReport;
    const locked = Boolean(selectedReport?.isComplete);
    const isDone = (s) => /complete|completed|done/i.test(String(s || ""));
    const byIncompleteFirst = (a, b) =>
      Number(isDone(a.status)) - Number(isDone(b.status));

    const statusChip = (s) =>
      isDone(s)
        ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
        : /progress/i.test(String(s || ""))
          ? "bg-sky-50 text-sky-700 ring-1 ring-sky-200"
          : "bg-gray-100 text-gray-600 ring-1 ring-gray-200";

    const rollUp = (main) => {
      const subs = Array.isArray(main.subWorks) ? main.subWorks : [];
      if (!subs.length) return main.status || "Pending";
      if (subs.every((s) => isDone(s.status))) return "Complete";
      if (subs.some((s) => /complete|progress/i.test(String(s.status || ""))))
        return "In Progress";
      return "Pending";
    };

    // Plain function (NOT a component) — called inline so the <input>s keep
    // focus across re-renders instead of remounting on every keystroke.
    const renderWorkCard = ({ rowKey, work, name, onSave, mainId, subId }) => {
      const done = isDone(work.status);
      const disabled = locked || done;
      const edit = getRowEdit(work);
      const history = sortSubWorkUpdates(work.updatedRemarks, {
        newestFirst: true,
      });
      return (
        <div
          key={rowKey}
          className={`rounded-xl border p-3 ${
            done
              ? "border-emerald-200 bg-emerald-50/60 opacity-80"
              : "border-gray-200 bg-white"
          }`}
        >
          <div className="mb-2 flex flex-wrap items-center gap-2 text-sm font-semibold text-gray-800">
            {name}
            <span
              className={`${statusChip(work.status)} rounded-full px-2.5 py-0.5 text-[11px] font-semibold`}
            >
              {work.status || "Pending"}
            </span>
            {done && (
              <span className="text-[11px] font-medium text-emerald-600">
                Locked
              </span>
            )}
            {renderEditUpdatesButton(work, name, { mainId, subId })}
          </div>
          {/* Super-admin sees updates only — no editing. */}
          {!isReviewer && (
            <div className="flex flex-wrap items-end gap-2">
              <div className="w-40">
                <label className={labelClass}>Status</label>
                <select
                  className={inputClass}
                  autoComplete="off"
                  value={edit.status || work.status || "Pending"}
                  onChange={(e) =>
                    setRowEditField(work.id, "status", e.target.value)
                  }
                  disabled={disabled}
                >
                  {FIELD_STATUS_OPTIONS.map((option) => (
                    <option key={option}>{option}</option>
                  ))}
                </select>
              </div>
              <div className="w-40">
                <label className={labelClass}>Date</label>
                <input
                  type="date"
                  className={inputClass}
                  autoComplete="off"
                  value={edit.date || toDateInputValue()}
                  onChange={(e) =>
                    setRowEditField(work.id, "date", e.target.value)
                  }
                  disabled={disabled}
                />
              </div>
              <div className="min-w-[220px] flex-1">
                <label className={labelClass}>Add Remark</label>
                <input
                  className={inputClass}
                  autoComplete="off"
                  value={edit.remarks || ""}
                  onChange={(e) =>
                    setRowEditField(work.id, "remarks", e.target.value)
                  }
                  placeholder="New dated note — added, not replaced"
                  disabled={disabled}
                />
              </div>
              <button
                type="button"
                className="rounded-full bg-amber-500 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={onSave}
                disabled={saving || disabled}
              >
                {saving ? "Saving..." : done ? "Done" : "Update"}
              </button>
            </div>
          )}
          {history.length > 0 && (
            <div
              className={`space-y-1 ${
                isReviewer ? "" : "mt-3 border-t border-gray-200 pt-2"
              }`}
            >
              {history.map((entry, i) => (
                <div key={i} className="text-[11px] text-gray-600">
                  <span className="font-semibold text-gray-700">
                    {formatDateLabel(entry.date)}
                  </span>
                  {entry.text ? ` — ${entry.text}` : ""}
                </div>
              ))}
            </div>
          )}
          {isReviewer && history.length === 0 && (
            <div className="text-[11px] text-gray-400">No updates yet.</div>
          )}
        </div>
      );
    };

    const mainWorks = [...(field.mainWorks || [])].sort((a, b) =>
      Number(isDone(rollUp(a))) - Number(isDone(rollUp(b))),
    );

    return (
      <div className="space-y-5">
        {field.unitName && (
          <div className="text-base font-semibold text-gray-800">
            Unit: {field.unitName}
          </div>
        )}
        {mainWorks.length === 0 && (
          <div className="rounded-xl border border-dashed border-gray-300 p-4 text-center text-sm text-gray-500">
            No work added yet.
          </div>
        )}
        {mainWorks.map((main) => {
          const subs = [...(main.subWorks || [])].sort(byIncompleteFirst);
          return (
            <div key={main.id} className="space-y-2">
              <div className="flex flex-wrap items-center gap-2 text-lg font-semibold text-gray-700">
                {main.mainWorkName || "Untitled work"}
                <span
                  className={`${statusChip(rollUp(main))} rounded-full px-2.5 py-0.5 text-xs font-semibold`}
                >
                  {rollUp(main)}
                </span>
              </div>
              {subs.length === 0
                ? renderWorkCard({
                    rowKey: "main",
                    work: main,
                    name: main.mainWorkName || "Untitled work",
                    onSave: () => handleSaveMainWorkRow(main),
                    mainId: main.id,
                  })
                : (
                  <div className="space-y-2">
                    {subs.map((sub) =>
                      renderWorkCard({
                        rowKey: sub.id,
                        work: sub,
                        name: sub.subWorkName || "Untitled sub-work",
                        onSave: () => handleSaveSubWorkRow(main.id, sub),
                        mainId: main.id,
                        subId: sub.id,
                      }),
                    )}
                  </div>
                )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderSheetBreakdown = (formType, formData) => {
    if (formType === 1) {
      if (isRevenueReport) return renderRevenueFieldBreakdown();
      const field = fieldReport;
      const mainWorks = field.mainWorks;

      // No per-sub-work completion figure is stored — approximate it from
      // status the same way the rest of the app treats these three states.
      const subWorkCompletion = (status) => {
        if (/complete/i.test(String(status || ""))) return 100;
        if (/progress/i.test(String(status || ""))) return 50;
        return 0;
      };
      const subStatusChip = (status) => {
        if (/complete/i.test(String(status || "")))
          return "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200";
        if (/progress/i.test(String(status || "")))
          return "bg-sky-50 text-sky-700 ring-1 ring-sky-200";
        return "bg-gray-100 text-gray-600 ring-1 ring-gray-200";
      };
      // A main work's shown status follows its sub-works, so the chip can't
      // read "Pending" while every sub-work is Complete.
      const mainRollUpStatus = (main) => {
        const subs = Array.isArray(main.subWorks) ? main.subWorks : [];
        if (!subs.length) return main.status || "Pending";
        if (subs.every((s) => /complete/i.test(String(s.status || ""))))
          return "Complete";
        if (
          subs.some((s) => /complete|progress/i.test(String(s.status || "")))
        )
          return "In Progress";
        return "Pending";
      };
      // Head-count that worked on a sub-work — the sum of every daily
      // update's peopleCount the employee logged against it.
      const subWorkPeople = (subWork) =>
        normalizeSubWorkUpdates(subWork.updatedRemarks).reduce(
          (sum, entry) =>
            sum + (entry.peopleCount === "" ? 0 : Number(entry.peopleCount) || 0),
          0,
        );
      // A main work's total manpower — the sum of every one of its sub-works'
      // head-counts.
      const mainWorkPeople = (main) =>
        (Array.isArray(main.subWorks) ? main.subWorks : []).reduce(
          (sum, sub) => sum + subWorkPeople(sub),
          0,
        );
      const latestUpdateFor = (subWork) => {
        const updates = sortSubWorkUpdates(subWork.updatedRemarks, {
          newestFirst: true,
        });
        return updates[0];
      };

      const anyUpdates = mainWorks.some((main) =>
        (main.subWorks || []).some(
          (subWork) => normalizeSubWorkUpdates(subWork.updatedRemarks).length,
        ),
      );

      return (
        <div className="space-y-5">
          {field.unitName && (
            <div
              className="font-semibold"
              style={{ fontSize: "16px", color: "#222222" }}
            >
              Unit: {field.unitName}
            </div>
          )}

          <div className="space-y-4">
            {mainWorks.map((main) => (
              <div key={main.id}>
                <div className="mb-1.5 flex flex-wrap items-center gap-2 text-xl font-semibold text-gray-700">
                  {main.mainWorkName || "Untitled work"}
                  {mainWorkPeople(main) > 0 && (
                    <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-sm font-semibold text-gray-600">
                      👥 {mainWorkPeople(main)} people total
                    </span>
                  )}
                  <OverdueBadge work={main} />
                  <OnHoldBadge work={main} />
                  <VerifiedBadge work={main} />
                  <WellDoneBadge work={main} />
                  {!(main.subWorks || []).length &&
                    renderEditUpdatesButton(
                      main,
                      main.mainWorkName || "Untitled work",
                      { mainId: main.id },
                    )}
                </div>
                {renderWorkDatesPanel({ work: main, mainId: main.id })}
                {renderVerificationPanel({ work: main, mainId: main.id })}
                {renderTable(
                  [
                    {
                      key: "subWorkName",
                      label: "Sub Work",
                      render: (r) => r.subWorkName || "—",
                    },
                    {
                      key: "startDate",
                      label: "Start",
                      render: (r) => formatDateLabel(r.startDate),
                    },
                    {
                      key: "completionDate",
                      label: "Deadline",
                      render: (r) => {
                        const info = getWorkOverdueInfo(r);
                        if (!info || !info.label)
                          return formatDateLabel(r.completionDate);
                        const tone =
                          info.state === "overdue"
                            ? "text-red-600"
                            : info.state === "completed-late" ||
                                info.state === "due-soon"
                              ? "text-amber-600"
                              : "text-slate-500";
                        return (
                          <span className="inline-flex flex-col">
                            <span>
                              {formatDateLabel(r.completionDate)}
                              {info.extended && (
                                <span className="text-sky-600">
                                  {" → "}
                                  {formatDateLabel(info.extendedDate)}
                                </span>
                              )}
                            </span>
                            <span
                              className={`text-[11px] font-semibold ${tone}`}
                            >
                              {info.label}
                            </span>
                          </span>
                        );
                      },
                    },
                    {
                      key: "alert",
                      label: "Alert (≤3 days)",
                      render: (r) => {
                        const info = getWorkOverdueInfo(r);
                        // "Due soon" already means: still open, effective
                        // deadline within 3 days (see getWorkOverdueInfo).
                        // Overdue / completed work isn't a forward-looking
                        // alert, so it's left blank here.
                        if (!info || info.state !== "due-soon") return "—";
                        return (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700 ring-1 ring-amber-200">
                            <i className="fa-solid fa-triangle-exclamation" />
                            {formatDateLabel(
                              info.effectiveDeadline || r.completionDate,
                            )}
                          </span>
                        );
                      },
                    },
                    { key: "status", label: "Status" },
                    {
                      key: "people",
                      label: "People",
                      render: (r) => subWorkPeople(r) || "—",
                    },
                    {
                      key: "updates",
                      label: "Last Update",
                      render: (r) => {
                        const latest = latestUpdateFor(r);
                        return latest?.date
                          ? formatDateLabel(latest.date)
                          : "—";
                      },
                    },
                    {
                      key: "completion",
                      label: "Completion",
                      render: (r) => `${subWorkCompletion(r.status)}%`,
                    },
                    ...(canEditWorkUpdates
                      ? [
                          {
                            key: "editUpdates",
                            label: "Updates",
                            render: (r) =>
                              renderEditUpdatesButton(
                                r,
                                `${main.mainWorkName || "Untitled work"} → ${
                                  r.subWorkName || "Sub-work"
                                }`,
                                { mainId: main.id, subId: r.id },
                              ) || "—",
                          },
                        ]
                      : []),
                  ],
                  main.subWorks,
                )}

                {/* Inline per-sub-work editors — update any sub-work in place */}
                {(main.subWorks || []).length > 0 && (
                  <div className="mt-3 space-y-2">
                    <h5 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">
                      Update Sub-works
                    </h5>
                    {main.subWorks.map((sub) => {
                      const edit = getRowEdit(sub);
                      return (
                        <div
                          key={sub.id}
                          className={`rounded-xl border p-3 ${
                            /(done|complete)/i.test(String(sub.status || ""))
                              ? "border-emerald-200 bg-emerald-50/70"
                              : "border-gray-200 bg-gray-50"
                          }`}
                        >
                          <div className="mb-2 flex flex-wrap items-center gap-2 text-sm font-semibold text-gray-700">
                            {sub.subWorkName || "Untitled sub-work"}
                            <OverdueBadge work={sub} />
                            <OnHoldBadge work={sub} />
                            <VerifiedBadge work={sub} />
                            <WellDoneBadge work={sub} />
                          </div>
                          <div className="flex flex-wrap items-end gap-2">
                            <div className="w-36">
                              <label className={labelClass}>Status</label>
                              <select
                                className={inputClass}
                                autoComplete="off"
                                value={edit.status || "Pending"}
                                onChange={(e) =>
                                  setRowEditField(
                                    sub.id,
                                    "status",
                                    e.target.value,
                                  )
                                }
                                disabled={selectedReport.isComplete}
                              >
                                {FIELD_STATUS_OPTIONS.map((option) => (
                                  <option key={option}>{option}</option>
                                ))}
                              </select>
                            </div>
                            <div className="w-40">
                              <label className={labelClass}>Date</label>
                              <input
                                type="date"
                                className={inputClass}
                                autoComplete="off"
                                value={edit.date || toDateInputValue()}
                                onChange={(e) =>
                                  setRowEditField(sub.id, "date", e.target.value)
                                }
                                disabled={selectedReport.isComplete}
                              />
                            </div>
                            <div className="w-24">
                              <label className={labelClass}>People</label>
                              <input
                                type="number"
                                min="0"
                                className={inputClass}
                                autoComplete="off"
                                value={edit.peopleCount}
                                onChange={(e) =>
                                  setRowEditField(
                                    sub.id,
                                    "peopleCount",
                                    e.target.value,
                                  )
                                }
                                placeholder="Count"
                                disabled={selectedReport.isComplete}
                              />
                            </div>
                            <div className="min-w-[200px] flex-1">
                              <label className={labelClass}>Add Remark</label>
                              <input
                                className={inputClass}
                                autoComplete="off"
                                value={edit.remarks || ""}
                                onChange={(e) =>
                                  setRowEditField(
                                    sub.id,
                                    "remarks",
                                    e.target.value,
                                  )
                                }
                                placeholder="New dated note — added, not replaced"
                                disabled={selectedReport.isComplete}
                              />
                            </div>
                            <button
                              type="button"
                              className="rounded-full bg-amber-500 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
                              onClick={() => handleSaveSubWorkRow(main.id, sub)}
                              disabled={saving || selectedReport.isComplete}
                            >
                              {saving
                                ? "Saving..."
                                : selectedReport.isComplete
                                  ? "Locked"
                                  : "Update"}
                            </button>
                          </div>
                          {renderWorkDatesPanel({
                            work: sub,
                            mainId: main.id,
                            subId: sub.id,
                          })}
                          {renderHoldPanel({
                            work: sub,
                            mainId: main.id,
                            subId: sub.id,
                          })}
                          {renderVerificationPanel({
                            work: sub,
                            mainId: main.id,
                            subId: sub.id,
                          })}
                          {renderRemarksPreview(
                            sub,
                            `${main.mainWorkName || "Untitled work"} → ${
                              sub.subWorkName || "Sub-work"
                            }`,
                            { mainId: main.id, subId: sub.id },
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Main work has no sub-works — update it directly. */}
                {(main.subWorks || []).length === 0 &&
                  (() => {
                    const edit = getRowEdit(main);
                    return (
                      <div className="mt-3 space-y-2">
                        <h5 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">
                          Update Work
                        </h5>
                        <div
                          className={`rounded-xl border p-3 ${
                            /(done|complete)/i.test(String(main.status || ""))
                              ? "border-emerald-200 bg-emerald-50/70"
                              : "border-gray-200 bg-gray-50"
                          }`}
                        >
                          <div className="flex flex-wrap items-end gap-2">
                            <div className="w-36">
                              <label className={labelClass}>Status</label>
                              <select
                                className={inputClass}
                                autoComplete="off"
                                value={edit.status || "Pending"}
                                onChange={(e) =>
                                  setRowEditField(
                                    main.id,
                                    "status",
                                    e.target.value,
                                  )
                                }
                                disabled={selectedReport.isComplete}
                              >
                                {FIELD_STATUS_OPTIONS.map((option) => (
                                  <option key={option}>{option}</option>
                                ))}
                              </select>
                            </div>
                            <div className="w-40">
                              <label className={labelClass}>Date</label>
                              <input
                                type="date"
                                className={inputClass}
                                autoComplete="off"
                                value={edit.date || toDateInputValue()}
                                onChange={(e) =>
                                  setRowEditField(
                                    main.id,
                                    "date",
                                    e.target.value,
                                  )
                                }
                                disabled={selectedReport.isComplete}
                              />
                            </div>
                            <div className="w-24">
                              <label className={labelClass}>People</label>
                              <input
                                type="number"
                                min="0"
                                className={inputClass}
                                autoComplete="off"
                                value={edit.peopleCount}
                                onChange={(e) =>
                                  setRowEditField(
                                    main.id,
                                    "peopleCount",
                                    e.target.value,
                                  )
                                }
                                placeholder="Count"
                                disabled={selectedReport.isComplete}
                              />
                            </div>
                            <div className="min-w-[200px] flex-1">
                              <label className={labelClass}>Add Remark</label>
                              <input
                                className={inputClass}
                                autoComplete="off"
                                value={edit.remarks || ""}
                                onChange={(e) =>
                                  setRowEditField(
                                    main.id,
                                    "remarks",
                                    e.target.value,
                                  )
                                }
                                placeholder="New dated note — added, not replaced"
                                disabled={selectedReport.isComplete}
                              />
                            </div>
                            <button
                              type="button"
                              className="rounded-full bg-amber-500 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
                              onClick={() => handleSaveMainWorkRow(main)}
                              disabled={saving || selectedReport.isComplete}
                            >
                              {saving
                                ? "Saving..."
                                : selectedReport.isComplete
                                  ? "Locked"
                                  : "Update"}
                            </button>
                          </div>
                          {renderHoldPanel({ work: main, mainId: main.id })}
                          {renderRemarksPreview(
                            main,
                            main.mainWorkName || "Untitled work",
                            { mainId: main.id },
                          )}
                        </div>
                      </div>
                    );
                  })()}

                {/* Add a new sub-work under this main work */}
                {!selectedReport.isComplete && (
                  <div className="mt-2 flex flex-wrap items-end gap-2">
                    <div className="min-w-[200px] flex-1">
                      <label className={labelClass}>New Sub-work</label>
                      <input
                        className={inputClass}
                        autoComplete="off"
                        value={newSubWorkNames[main.id] || ""}
                        onChange={(e) =>
                          setNewSubWorkNames((prev) => ({
                            ...prev,
                            [main.id]: e.target.value,
                          }))
                        }
                        placeholder="Conduiting / Wiring / Testing"
                      />
                    </div>
                    <button
                      type="button"
                      className="rounded-full border-2 border-amber-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-amber-700 transition hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-60"
                      onClick={() => handleAddFieldSubWork(main.id)}
                      disabled={
                        saving || !(newSubWorkNames[main.id] || "").trim()
                      }
                    >
                      + Add sub-work
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Add a new main work to this unit's sheet — no second sheet needed */}
          {!selectedReport.isComplete && (
            <div className="flex flex-wrap items-end gap-2 rounded-2xl border border-dashed border-gray-300 p-3">
              <div className="min-w-[220px] flex-1">
                <label className={labelClass}>New Main Work</label>
                <input
                  className={inputClass}
                  autoComplete="off"
                  value={newMainWorkName}
                  onChange={(e) => setNewMainWorkName(e.target.value)}
                  placeholder="Electrical / Plumbing / Civil / Finishing"
                />
              </div>
              <button
                type="button"
                className="rounded-full bg-amber-500 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={handleAddFieldMainWork}
                disabled={saving || !newMainWorkName.trim()}
              >
                + Add main work
              </button>
            </div>
          )}

          {anyUpdates && (
            <div>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                Sub-work Updates
              </h4>
              <div className="space-y-5">
                {mainWorks.map((main) => {
                  const subsWithHistory = (main.subWorks || []).filter(
                    (subWork) =>
                      normalizeSubWorkUpdates(subWork.updatedRemarks).length,
                  );
                  if (!subsWithHistory.length) return null;
                  return (
                    <div key={main.id} className="space-y-3">
                      <div className="flex flex-wrap items-center gap-2 text-xl font-semibold text-gray-700">
                        {main.mainWorkName || "Untitled work"}
                        <span
                          className={`${subStatusChip(mainRollUpStatus(main))} rounded-full px-2.5 py-1 text-xs font-semibold`}
                        >
                          {mainRollUpStatus(main)}
                        </span>
                        {mainWorkPeople(main) > 0 && (
                          <span className="rounded-full bg-gray-100 px-2.5 py-1 text-sm font-semibold text-gray-600">
                            👥 {mainWorkPeople(main)} people total
                          </span>
                        )}
                        <OverdueBadge work={main} />
                        <OnHoldBadge work={main} />
                        <VerifiedBadge work={main} />
                        <WellDoneBadge work={main} />
                      </div>
                      {subsWithHistory.map((subWork) => (
                        <div key={subWork.id} className="ml-3">
                          <div className="mb-1.5 flex flex-wrap items-center gap-2 text-xl font-medium text-gray-500">
                            {subWork.subWorkName || "Sub-work"}
                            <span
                              className={`${subStatusChip(subWork.status)} rounded-full px-2.5 py-1 text-sm font-semibold`}
                            >
                              {subWork.status || "Pending"}
                            </span>
                            <span className="rounded-full bg-gray-100 px-2.5 py-1 text-sm font-semibold text-gray-600">
                              👥 {subWorkPeople(subWork)} people total
                            </span>
                            <OverdueBadge work={subWork} />
                            <OnHoldBadge work={subWork} />
                            <VerifiedBadge work={subWork} />
                            <WellDoneBadge work={subWork} />
                          </div>
                          {renderRemarksPreview(
                            subWork,
                            `${main.mainWorkName || "Untitled work"} → ${
                              subWork.subWorkName || "Sub-work"
                            }`,
                            { mainId: main.id, subId: subWork.id },
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      );
    }

    if (formType === 2) {
      return renderMetricsMatrix(formData?.metrics);
    }

    if (formType === 3) {
      return (
        <div className="space-y-5">
          {renderMetricsMatrix(formData?.metrics)}
          {renderContactBreakdown(formData, { withStats: false })}
        </div>
      );
    }

    if (formType === 8) {
      // Manager Netplus daily sheet: one row per figure, one column per date.
      // Cumulative figures read "day (total till date)" in one cell.
      const entries = withNetplusMgrRunningTotals(formData?.entries);
      const rows = NETPLUS_MGR_FIELDS.map(({ key, label, cumulative }) => ({
        key,
        label: cumulative ? `${label} (total till date)` : label,
        entries: entries.map((e) => {
          const day = String(e[key] ?? "").trim();
          return {
            date: e.date,
            savedAt: e.savedAt,
            value: cumulative
              ? day || e.running[key]
                ? `${day || 0} (${e.running[key]})`
                : ""
              : e[key],
          };
        }),
      }));
      return renderMetricsMatrix(rows);
    }

    if (formType === 7) {
      // Flatten the fixed areas into "Area <code> — <Status>" rows so the
      // generic date-columns matrix renders them unchanged.
      const areas = Array.isArray(formData?.areas) ? formData.areas : [];
      const rows = areas.flatMap((area) =>
        NETPLUS_STATUS_FIELDS.map(({ key, label }) => ({
          key: `${area.code}-${key}`,
          label: `Area ${area.code} — ${label}`,
          entries: (Array.isArray(area.entries) ? area.entries : []).map(
            (entry) => ({ date: entry.date, savedAt: entry.savedAt, value: entry[key] }),
          ),
        })),
      );
      return renderMetricsMatrix(rows);
    }

    if (formType === 4) {
      const customerDealerLog = Array.isArray(formData?.customerDealerLog)
        ? formData.customerDealerLog
        : [];
      return (
        <div className="space-y-5">
          {Array.isArray(formData?.metrics) &&
            formData.metrics.length > 0 &&
            renderMetricsMatrix(formData.metrics)}
          {renderContactBreakdown(formData, { withStats: true })}
          {customerDealerLog.length > 0 && (
            <div>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                Customer / Dealer Log (legacy)
              </h4>
              {renderTable(
                [
                  {
                    key: "date",
                    label: "Date",
                    render: (r) => formatDateLabel(r.date),
                  },
                  { key: "customerName", label: "Customer Name" },
                  { key: "customerPhone", label: "Customer Phone" },
                  { key: "dealerName", label: "Dealer Name" },
                  { key: "dealerPhone", label: "Dealer Phone" },
                ],
                customerDealerLog,
              )}
            </div>
          )}
        </div>
      );
    }

    if (formType === 5) {
      return renderTable(
        [
          {
            key: "assignDate",
            label: "Align Date",
            render: (r) => formatDateLabel(r.assignDate),
          },
          { key: "work", label: "Work" },
          {
            key: "dueDate",
            label: "Due Date",
            render: (r) => formatDateLabel(r.dueDate),
          },
          { key: "status", label: "Status" },
          {
            key: "remarks",
            label: "Remarks",
            render: (r) => r.remarks || "—",
          },
        ],
        formData?.tasks,
      );
    }

    if (formType === 6) {
      if (isRevenueReport) return renderRevenueSheetBreakdown();
      const revenueTasks = Array.isArray(formData?.tasks)
        ? formData.tasks
        : [];
      const otherWorksList = normalizeOtherWorks(formData?.otherWorks).filter(
        (entry) => entry.text.trim(),
      );

      // Only add the Technical Staff column when at least one task actually
      // has that field filled in — an entirely blank column is dropped from
      // the table rather than showing "—" everywhere.
      const hasTechnicalStaff = revenueTasks.some((task) =>
        technicalStaffById.get(task.assignedTechnicalStaff),
      );

      const taskColumns = [
        {
          key: "workAssignDate",
          label: "Work Assign Date",
          render: (r) => formatDateLabel(r.workAssignDate),
        },
        { key: "work", label: "Work" },
        {
          key: "workDueDate",
          label: "Due Date",
          render: (r) => formatDateLabel(r.workDueDate),
        },
        { key: "status", label: "Status" },
        ...(hasTechnicalStaff
          ? [
              {
                key: "assignedTechnicalStaff",
                label: "Technical Staff",
                render: (r) => {
                  const person = technicalStaffById.get(
                    r.assignedTechnicalStaff,
                  );
                  return person
                    ? `${person.name} - ${formatRoleLabel(person.role)}`
                    : "—";
                },
              },
            ]
          : []),
        {
          key: "remarks",
          label: "Remarks",
          scrollable: true,
          render: (r) => r.remarks || "—",
        },
      ];

      // Date | Remarks table shape shared by the two dated-history sections
      // below — one row per date, every note from that date grouped into
      // its Remarks cell instead of one row per note.
      const remarksHistoryColumns = [
        {
          key: "date",
          label: "Date",
          render: (r) => formatDateLabel(r.date),
        },
        {
          key: "texts",
          label: "Remarks",
          render: (r) => r.texts.join(" | "),
        },
      ];

      const tasksWithSubWorks = revenueTasks.filter(
        (task) => Array.isArray(task.subWorks) && task.subWorks.length,
      );

      const tasksWithRemarksHistory = revenueTasks.filter(
        (task) => normalizeRemarksHistory(task.updatedRemarks).length,
      );

      const subWorkHistoryGroups = tasksWithSubWorks
        .map((task) => ({
          task,
          subWorks: task.subWorks.filter(
            (subWork) => normalizeRemarksHistory(subWork.updatedRemarks).length,
          ),
        }))
        .filter((group) => group.subWorks.length);

      return (
        <div className="space-y-5">
          {renderTable(taskColumns, revenueTasks)}

          {tasksWithSubWorks.length > 0 && (
            <div>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                Sub Works
              </h4>
              <div className="space-y-4">
                {tasksWithSubWorks.map((task, index) => (
                  <div key={index}>
                    <div className="mb-1.5 text-xl font-semibold text-gray-700">
                      {task.work || `Task ${index + 1}`}
                    </div>
                    {renderTable(
                      [
                        { key: "subWorkName", label: "Sub-work" },
                        {
                          key: "workStartDate",
                          label: "Start",
                          render: (r) => formatDateLabel(r.workStartDate),
                        },
                        {
                          key: "workExtendDate",
                          label: "Extend",
                          render: (r) => formatDateLabel(r.workExtendDate),
                        },
                        { key: "status", label: "Status" },
                      ],
                      task.subWorks,
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {tasksWithRemarksHistory.length > 0 && (
            <div>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                Updated Remarks
              </h4>
              <div className="space-y-4">
                {tasksWithRemarksHistory.map((task, index) => (
                  <div key={index}>
                    <div className="mb-1.5 text-xl font-semibold text-gray-700">
                      {task.work || `Task ${index + 1}`}
                    </div>
                    {renderTable(
                      remarksHistoryColumns,
                      groupRemarksByDate(task.updatedRemarks),
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {subWorkHistoryGroups.length > 0 && (
            <div>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                Sub-work Updates
              </h4>
              <div className="space-y-5">
                {subWorkHistoryGroups.map(({ task, subWorks }, index) => (
                  <div key={index} className="space-y-3">
                    <div className="text-xl font-semibold text-gray-700">
                      {task.work || `Task ${index + 1}`}
                    </div>
                    {subWorks.map((subWork, subIndex) => (
                      <div key={subIndex} className="ml-3">
                        <div className="mb-1.5 text-xl font-medium text-gray-500">
                          {subWork.subWorkName || `Sub-work ${subIndex + 1}`}
                        </div>
                        {renderTable(
                          remarksHistoryColumns,
                          groupRemarksByDate(subWork.updatedRemarks),
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
              Other Work
            </h4>
            {otherWorksList.length ? (
              <ul className="list-disc space-y-1 rounded-xl border border-gray-200 bg-white px-8 py-3 text-sm text-gray-700">
                {otherWorksList.map((entry, i) => (
                  <li key={i}>
                    <span className="font-medium text-gray-500">
                      {formatDateLabel(entry.date)}:
                    </span>{" "}
                    {entry.text}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="rounded-xl border border-dashed border-gray-300 p-4 text-center text-sm text-gray-500">
                No other work recorded yet.
              </div>
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  const renderReadOnlyBlock = () => {
    if (reportLoading) {
      return (
        <div className={`${panelClass} p-6 text-center text-gray-500`}>
          Loading report...
        </div>
      );
    }

    if (!selectedReport) {
      return (
        <div className={`${panelClass} p-6 text-center text-gray-500`}>
          Report not found.
        </div>
      );
    }

    const formType = Number(selectedReport.formType);
    const formData = selectedReport.formData || {};

    // Field Execution Journal (formType 1) — cascading Unit → Main Work →
    // Sub Work pickers all read off the memoized fieldReport tree (legacy
    // flat sheets are folded into it too, so both shapes render the same
    // editor).
    const activeFieldMainWork = fieldReport.mainWorks.find(
      (mainWork) => mainWork.id === fieldMainWorkId,
    );
    const activeFieldSubWorks = Array.isArray(activeFieldMainWork?.subWorks)
      ? activeFieldMainWork.subWorks
      : [];

    const salesMetrics =
      formType === 4 && Array.isArray(editForm.metrics) ? editForm.metrics : [];
    const activeSalesMetric =
      salesMetrics.find((metric) => metric.key === salesMetricTarget) ||
      salesMetrics[0];
    const salesMetricValue = activeSalesMetric
      ? getMetricEntryValue(activeSalesMetric, salesEntryDate)
      : "";

    const tasks =
      formType === 5 || formType === 6
        ? Array.isArray(formData?.tasks)
          ? formData.tasks
          : []
        : [];
    const selectedTask = tasks[selectedTaskIndex];

    const subWorksForCheck =
      formType === 1
        ? fieldReport.mainWorks.flatMap((mainWork) =>
            Array.isArray(mainWork.subWorks) ? mainWork.subWorks : [],
          )
        : [];
    const allSubWorksComplete =
      formType === 1 &&
      subWorksForCheck.length > 0 &&
      subWorksForCheck.every((subWork) =>
        /complete/i.test(String(subWork.status || "")),
      );

    return (
      <div className={`${panelClass} p-5 md:p-6`}>
        {/* Full-width inline quick edit — hidden once every sub-work is
            complete (nothing left to update), and skipped entirely for Real
            Estate Sales: its Status/Month/Project/Date/Value/Remarks row
            isn't needed there, so only the Sheet Breakdown view below shows. */}
        {/* Revenue edits every work from its own card in the breakdown below,
            so the top quick-editor is hidden for it. */}
        {formType !== 4 &&
        !(isRevenueReport && (formType === 1 || formType === 6)) &&
        (allSubWorksComplete ? (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-sm font-semibold text-emerald-700">
            Work is completed on{" "}
            {formatDateLabel(
              selectedReport.completedAt ||
                selectedReport.lastEditedAt ||
                selectedReport.updatedAt,
            )}
          </div>
        ) : (
          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
            {formType === 5 || formType === 6 ? (
              /* Task picker: select a task by its work description, then edit
               that specific task's status/remarks — no free typing. */
              <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[220px] flex-1">
                  <label className={labelClass}>Work</label>
                  <select
                    className={inputClass}
                    autoComplete="off"
                    value={selectedTaskIndex}
                    onChange={(event) =>
                      handleSelectTask(Number(event.target.value))
                    }
                    disabled={!tasks.length}
                  >
                    {!tasks.length && <option>No tasks added yet</option>}
                    {tasks.map((task, index) => (
                      <option key={index} value={index}>
                        {task.work || `Task ${index + 1}`}
                      </option>
                    ))}
                  </select>
                </div>

                {formType === 6 &&
                  Array.isArray(selectedTask?.subWorks) &&
                  selectedTask.subWorks.length > 0 && (
                    <div className="min-w-[180px] flex-1">
                      <label className={labelClass}>Sub-work</label>
                      <select
                        className={inputClass}
                        autoComplete="off"
                        value={selectedSubWorkIndex}
                        onChange={(event) =>
                          handleSelectSubWork(event.target.value)
                        }
                        disabled={selectedReport.isComplete}
                      >
                        <option value="">Whole task</option>
                        {selectedTask.subWorks.map((subWork, index) => (
                          <option key={index} value={index}>
                            {subWork.subWorkName || `Sub-work ${index + 1}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                <div className="w-40">
                  <label className={labelClass}>Status</label>
                  <select
                    className={inputClass}
                    autoComplete="off"
                    value={taskEdits.status || "Pending"}
                    onChange={(event) =>
                      handleEditTaskField("status", event.target.value)
                    }
                    disabled={!tasks.length || selectedReport.isComplete}
                  >
                    {formType === 6 && selectedSubWorkIndex !== "" ? (
                      <>
                        <option>Pending</option>
                        <option>In Progress</option>
                        <option>Complete</option>
                      </>
                    ) : (
                      <>
                        <option>Pending</option>
                        <option>In Progress</option>
                        <option>Done</option>
                        <option>Under Review</option>
                      </>
                    )}
                  </select>
                </div>

                {formType === 6 && (
                  <div className="w-44">
                    <label className={labelClass}>Date</label>
                    <input
                      type="date"
                      className={inputClass}
                      autoComplete="off"
                      value={taskEdits.date || toDateInputValue()}
                      onChange={(event) =>
                        handleEditTaskField("date", event.target.value)
                      }
                      disabled={!tasks.length || selectedReport.isComplete}
                    />
                  </div>
                )}

                <div className="min-w-[220px] flex-1">
                  <label className={labelClass}>
                    {formType === 6 ? "Add Remark" : "Remarks"}
                  </label>
                  <input
                    className={inputClass}
                    autoComplete="off"
                    value={taskEdits.remarks || ""}
                    onChange={(event) =>
                      handleEditTaskField("remarks", event.target.value)
                    }
                    placeholder={
                      formType === 6
                        ? "New dated note — added, not replaced"
                        : "Quick follow-up note"
                    }
                    disabled={!tasks.length || selectedReport.isComplete}
                  />
                </div>

                <button
                  type="button"
                  className="rounded-full bg-amber-500 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={handleSaveTask}
                  disabled={
                    saving || !tasks.length || selectedReport.isComplete
                  }
                >
                  {saving
                    ? "Saving..."
                    : selectedReport.isComplete
                      ? "Locked"
                      : "Save"}
                </button>

                {formType === 6 && (
                  <div className="w-full">
                    <div className="mb-2 flex items-center justify-between">
                      <label className={labelClass}>Other Work</label>
                      <button
                        type="button"
                        title="Add other work"
                        className="flex h-7 w-7 items-center justify-center rounded-full bg-amber-500 text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
                        onClick={handleAddOtherWork}
                        disabled={selectedReport.isComplete}
                      >
                        <i className="fa-solid fa-plus text-xs" />
                      </button>
                    </div>
                    <div className="flex gap-3 overflow-x-auto pb-2">
                      {otherWorks.map((entry, index) => (
                        <div
                          key={index}
                          className="flex w-64 flex-shrink-0 flex-col gap-2 rounded-xl border border-gray-200 bg-white p-3"
                        >
                          <div className="flex items-center gap-2">
                            <input
                              type="date"
                              className={`${inputClass} flex-1`}
                              autoComplete="off"
                              value={entry.date || toDateInputValue()}
                              onChange={(event) =>
                                handleUpdateOtherWork(
                                  index,
                                  "date",
                                  event.target.value,
                                )
                              }
                              disabled={selectedReport.isComplete}
                            />
                            <button
                              type="button"
                              title="Remove"
                              className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl border-2 border-amber-500 text-gray-500 transition hover:text-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
                              onClick={() => handleRemoveOtherWork(index)}
                              disabled={selectedReport.isComplete}
                            >
                              <i className="fa-solid fa-xmark text-xs" />
                            </button>
                          </div>
                          <input
                            className={inputClass}
                            autoComplete="off"
                            value={entry.text}
                            onChange={(event) =>
                              handleUpdateOtherWork(
                                index,
                                "text",
                                event.target.value,
                              )
                            }
                            placeholder="Other work item"
                            disabled={selectedReport.isComplete}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : formType === 1 ? (
              /* Field Execution Journal: pick Unit → Main Work → Sub Work,
                 then log that sub-work's status + a dated daily note with the
                 head-count that worked on it. */
              <div className="flex flex-wrap items-end gap-3">
                <div className={editingUnit ? "w-64" : "w-44"}>
                  <label className={labelClass}>Unit</label>
                  {editingUnit ? (
                    <div className="flex items-center gap-1.5">
                      <input
                        className={inputClass}
                        autoComplete="off"
                        value={unitNameDraft}
                        onChange={(event) =>
                          setUnitNameDraft(event.target.value)
                        }
                        placeholder="Villa 87 / Plot 7 / Block A-3"
                        disabled={selectedReport.isComplete}
                      />
                      <button
                        type="button"
                        title="Save unit name"
                        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-amber-500 text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
                        onClick={handleSaveUnitName}
                        disabled={saving || !unitNameDraft.trim()}
                      >
                        <i className="fa-solid fa-check text-xs" />
                      </button>
                      <button
                        type="button"
                        title="Cancel"
                        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl border-2 border-amber-500 text-gray-500 transition hover:text-amber-700"
                        onClick={() => setEditingUnit(false)}
                      >
                        <i className="fa-solid fa-xmark text-xs" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <select
                        className={inputClass}
                        autoComplete="off"
                        value={fieldReport.unitName || ""}
                        disabled
                      >
                        <option>{fieldReport.unitName || "—"}</option>
                      </select>
                      <button
                        type="button"
                        title="Edit unit name"
                        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl border-2 border-amber-500 text-gray-500 transition hover:text-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
                        onClick={startEditUnit}
                        disabled={selectedReport.isComplete}
                      >
                        <i className="fa-solid fa-pen text-xs" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="min-w-[200px] flex-1">
                  <label className={labelClass}>Main Work</label>
                  <select
                    className={inputClass}
                    autoComplete="off"
                    value={fieldMainWorkId}
                    onChange={(event) =>
                      handleSelectFieldMainWork(event.target.value)
                    }
                    disabled={
                      selectedReport.isComplete || !fieldReport.mainWorks.length
                    }
                  >
                    {!fieldReport.mainWorks.length && (
                      <option value="">No main works</option>
                    )}
                    {fieldReport.mainWorks.map((mainWork) => (
                      <option key={mainWork.id} value={mainWork.id}>
                        {mainWork.mainWorkName || "Untitled work"}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="min-w-[180px] flex-1">
                  <label className={labelClass}>Sub Work</label>
                  <select
                    className={inputClass}
                    autoComplete="off"
                    value={fieldSubWorkId}
                    onChange={(event) =>
                      handleSelectFieldSubWork(event.target.value)
                    }
                    disabled={
                      selectedReport.isComplete || !activeFieldSubWorks.length
                    }
                  >
                    {!activeFieldSubWorks.length && (
                      <option value="">No sub works</option>
                    )}
                    {activeFieldSubWorks.map((subWork) => (
                      <option key={subWork.id} value={subWork.id}>
                        {subWork.subWorkName || "Untitled sub-work"}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="w-40">
                  <label className={labelClass}>Status</label>
                  <select
                    className={inputClass}
                    autoComplete="off"
                    value={fieldEdits.status || "Pending"}
                    onChange={(event) =>
                      handleEditFieldEditsField("status", event.target.value)
                    }
                    disabled={
                      selectedReport.isComplete || !activeFieldSubWorks.length
                    }
                  >
                    {FIELD_STATUS_OPTIONS.map((option) => (
                      <option key={option}>{option}</option>
                    ))}
                  </select>
                </div>

                <div className="w-40">
                  <label className={labelClass}>Date</label>
                  <input
                    type="date"
                    className={inputClass}
                    autoComplete="off"
                    value={fieldEdits.date || toDateInputValue()}
                    onChange={(event) =>
                      handleEditFieldEditsField("date", event.target.value)
                    }
                    disabled={selectedReport.isComplete}
                  />
                </div>

                <div className="w-28">
                  <label className={labelClass}>People</label>
                  <input
                    type="number"
                    min="0"
                    className={inputClass}
                    autoComplete="off"
                    value={fieldEdits.peopleCount}
                    onChange={(event) =>
                      handleEditFieldEditsField(
                        "peopleCount",
                        event.target.value,
                      )
                    }
                    placeholder="Count"
                    disabled={
                      selectedReport.isComplete || !activeFieldSubWorks.length
                    }
                  />
                </div>

                <div className="min-w-[220px] flex-1">
                  <label className={labelClass}>Add Remark</label>
                  <input
                    className={inputClass}
                    autoComplete="off"
                    value={fieldEdits.remarks || ""}
                    onChange={(event) =>
                      handleEditFieldEditsField("remarks", event.target.value)
                    }
                    placeholder="New dated note — added, not replaced"
                    disabled={
                      selectedReport.isComplete || !activeFieldSubWorks.length
                    }
                  />
                </div>

                <button
                  type="button"
                  className="rounded-full bg-amber-500 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={handleSaveField}
                  disabled={
                    saving ||
                    selectedReport.isComplete ||
                    !activeFieldSubWorks.length
                  }
                >
                  {saving
                    ? "Saving..."
                    : selectedReport.isComplete
                      ? "Locked"
                      : "Save"}
                </button>
              </div>
            ) : formType === 7 ? (
              /* Netplus Updates: pick a day, then set each area's status
                 counts for that day. Other days stay untouched. */
              <div className="space-y-4">
                <div className="flex flex-wrap items-end gap-3">
                  <div className="w-44">
                    <label className={labelClass}>Day</label>
                    <input
                      type="date"
                      className={inputClass}
                      autoComplete="off"
                      value={netplusDate}
                      max={toDateInputValue()}
                      onChange={(event) =>
                        event.target.value && setNetplusDate(event.target.value)
                      }
                      disabled={selectedReport.isComplete}
                    />
                  </div>
                  <div className="w-40">
                    <label className={labelClass}>Status</label>
                    <select
                      className={inputClass}
                      autoComplete="off"
                      value={
                        editForm.status || selectedReport.status || "Pending"
                      }
                      onChange={(event) =>
                        handleEditField("status", event.target.value)
                      }
                      disabled={selectedReport.isComplete}
                    >
                      <option>Pending</option>
                      <option>In Progress</option>
                      <option>Complete</option>
                    </select>
                  </div>
                  <div className="min-w-[220px] flex-1">
                    <label className={labelClass}>Remarks</label>
                    <input
                      className={inputClass}
                      autoComplete="off"
                      value={editForm.remarks || ""}
                      onChange={(event) =>
                        handleEditField("remarks", event.target.value)
                      }
                      placeholder="Quick note"
                      disabled={selectedReport.isComplete}
                    />
                  </div>
                </div>
                {(Array.isArray(editForm.areas) ? editForm.areas : []).map(
                  (area) => {
                    const entry = getNetplusEntry(area, netplusDate);
                    return (
                      <div
                        key={area.code}
                        className="rounded-2xl border border-gray-200 bg-white p-4"
                      >
                        <div className="mb-3 text-sm font-semibold text-gray-700">
                          Area {area.code}
                        </div>
                        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
                          {NETPLUS_STATUS_FIELDS.map(({ key, label }) => (
                            <div key={key}>
                              <label className={labelClass}>{label}</label>
                              <input
                                type="number"
                                min="0"
                                className={inputClass}
                                autoComplete="off"
                                value={entry[key] ?? ""}
                                onChange={(event) =>
                                  handleNetplusEditValue(
                                    area.code,
                                    key,
                                    event.target.value,
                                  )
                                }
                                placeholder="0"
                                disabled={selectedReport.isComplete}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  },
                )}
                <button
                  type="button"
                  className="rounded-full bg-amber-500 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={handleSave}
                  disabled={saving || selectedReport.isComplete}
                >
                  {saving
                    ? "Saving..."
                    : selectedReport.isComplete
                      ? "Locked"
                      : "Save"}
                </button>
              </div>
            ) : (
              /* Single-line inline editor: status + form-specific field + remarks + save */
              <div className="flex flex-wrap items-end gap-3">
                <div className="w-40">
                  <label className={labelClass}>Status</label>
                  <select
                    className={inputClass}
                    autoComplete="off"
                    value={
                      editForm.status || selectedReport.status || "Pending"
                    }
                    onChange={(event) =>
                      handleEditField("status", event.target.value)
                    }
                    disabled={selectedReport.isComplete}
                  >
                    <option>Pending</option>
                    <option>In Progress</option>
                    <option>Complete</option>
                    <option>Completed</option>
                    <option>Under Review</option>
                  </select>
                </div>

                {(formType === 2 || formType === 3 || formType === 4) && (
                  <div className="w-44">
                    <label className={labelClass}>Month</label>
                    <input
                      type="month"
                      className={inputClass}
                      autoComplete="off"
                      value={editForm.month || ""}
                      onChange={(event) =>
                        handleEditField("month", event.target.value)
                      }
                      disabled={selectedReport.isComplete}
                    />
                  </div>
                )}

                {formType === 4 && (
                  <>
                    <div className="min-w-[220px] flex-1">
                      <label className={labelClass}>Project / Open Visit</label>
                      <select
                        className={inputClass}
                        autoComplete="off"
                        value={salesMetricTarget || activeSalesMetric?.key || ""}
                        onChange={(event) =>
                          setSalesMetricTarget(event.target.value)
                        }
                        disabled={selectedReport.isComplete}
                      >
                        {!salesMetrics.length && (
                          <option>No projects available</option>
                        )}
                        {salesMetrics.map((metric) => (
                          <option key={metric.key} value={metric.key}>
                            {metric.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="w-40">
                      <label className={labelClass}>Date</label>
                      <input
                        type="date"
                        className={inputClass}
                        autoComplete="off"
                        value={salesEntryDate}
                        max={toDateInputValue()}
                        onChange={(event) =>
                          setSalesEntryDate(event.target.value)
                        }
                        disabled={selectedReport.isComplete}
                      />
                    </div>
                    <div className="w-32">
                      <label className={labelClass}>Value</label>
                      <input
                        className={inputClass}
                        autoComplete="off"
                        value={salesMetricValue}
                        onChange={(event) =>
                          handleSalesValueChange(event.target.value)
                        }
                        placeholder="Count"
                        disabled={selectedReport.isComplete}
                      />
                    </div>
                  </>
                )}

                <div className="min-w-[220px] flex-1">
                  <label className={labelClass}>Remarks</label>
                  <input
                    className={inputClass}
                    autoComplete="off"
                    value={editForm.remarks || ""}
                    onChange={(event) =>
                      handleEditField("remarks", event.target.value)
                    }
                    placeholder="Quick follow-up note"
                    disabled={selectedReport.isComplete}
                  />
                </div>

                <button
                  type="button"
                  className="rounded-full bg-amber-500 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={handleSave}
                  disabled={saving || selectedReport.isComplete}
                >
                  {saving
                    ? "Saving..."
                    : selectedReport.isComplete
                      ? "Locked"
                      : "Save"}
                </button>
              </div>
            )}
          </div>
        ))}

        {/* Real Estate Sales — legacy Customer / Dealer Log. Superseded by the
            Contact list below; only shown for sheets that already hold
            dealer-log rows so their history stays editable. */}
        {formType === 4 &&
          Array.isArray(selectedReport.formData?.customerDealerLog) &&
          selectedReport.formData.customerDealerLog.length > 0 && (
          <div className="mt-4 rounded-2xl border border-gray-200 bg-gray-50 p-5">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h4 className={labelClass}>Customer / Dealer Log (legacy)</h4>
                <p className="text-xs text-gray-400">
                  Optional — customer and dealer contact details per visit.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="rounded-full border-2 border-amber-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-gray-700 transition hover:text-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={handleAddCustomerDealer}
                  disabled={selectedReport.isComplete}
                >
                  + Add Row
                </button>
                <button
                  type="button"
                  className="rounded-full bg-amber-500 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={handleSave}
                  disabled={saving || selectedReport.isComplete}
                >
                  {saving
                    ? "Saving..."
                    : selectedReport.isComplete
                      ? "Locked"
                      : "Save"}
                </button>
              </div>
            </div>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 text-gray-500">
                  <tr>
                    <th className="px-3 py-1.5 text-left font-medium">Date</th>
                    <th className="px-3 py-1.5 text-left font-medium">
                      Customer Name
                    </th>
                    <th className="px-3 py-1.5 text-left font-medium">
                      Customer Phone
                    </th>
                    <th className="px-3 py-1.5 text-left font-medium">
                      Dealer Name
                    </th>
                    <th className="px-3 py-1.5 text-left font-medium">
                      Dealer Phone
                    </th>
                    <th className="px-3 py-1.5 text-right font-medium">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {(Array.isArray(editForm.customerDealerLog)
                    ? editForm.customerDealerLog
                    : []
                  ).length === 0 && (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-3 py-4 text-center text-xs text-gray-400"
                      >
                        No rows added yet.
                      </td>
                    </tr>
                  )}
                  {(Array.isArray(editForm.customerDealerLog)
                    ? editForm.customerDealerLog
                    : []
                  ).map((row, index) => (
                    <tr key={index}>
                      <td className="px-3 py-1.5">
                        <input
                          className={inputClass}
                          autoComplete="off"
                          type="date"
                          value={row.date || ""}
                          onChange={(event) =>
                            handleUpdateCustomerDealer(
                              index,
                              "date",
                              event.target.value,
                            )
                          }
                          disabled={selectedReport.isComplete}
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <input
                          className={inputClass}
                          autoComplete="off"
                          value={row.customerName || ""}
                          onChange={(event) =>
                            handleUpdateCustomerDealer(
                              index,
                              "customerName",
                              event.target.value,
                            )
                          }
                          placeholder="Customer name"
                          disabled={selectedReport.isComplete}
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <input
                          className={inputClass}
                          autoComplete="off"
                          value={row.customerPhone || ""}
                          onChange={(event) =>
                            handleUpdateCustomerDealer(
                              index,
                              "customerPhone",
                              event.target.value,
                            )
                          }
                          placeholder="Customer phone"
                          disabled={selectedReport.isComplete}
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <input
                          className={inputClass}
                          autoComplete="off"
                          value={row.dealerName || ""}
                          onChange={(event) =>
                            handleUpdateCustomerDealer(
                              index,
                              "dealerName",
                              event.target.value,
                            )
                          }
                          placeholder="Dealer name"
                          disabled={selectedReport.isComplete}
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <input
                          className={inputClass}
                          autoComplete="off"
                          value={row.dealerPhone || ""}
                          onChange={(event) =>
                            handleUpdateCustomerDealer(
                              index,
                              "dealerPhone",
                              event.target.value,
                            )
                          }
                          placeholder="Dealer phone"
                          disabled={selectedReport.isComplete}
                        />
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        <button
                          type="button"
                          className="rounded-xl border-2 border-amber-500 px-3 py-2 text-xs font-semibold text-gray-600 hover:text-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
                          onClick={() => handleRemoveCustomerDealer(index)}
                          disabled={selectedReport.isComplete}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Tele Calling / Real Estate Sales — summary + (Sales) manual daily
            counters; the contact roster, call log and remarks are managed
            from the Customer Database page and shown here read-only below. */}
        {formType === 3 && renderCallingMetrics()}
        {(formType === 3 || formType === 4) && renderReportingSummary(formType)}

        {/* Full-width sheet breakdown */}
        <div className="mt-6">
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-gray-500">
            Sheet Breakdown
          </h3>
          {renderSheetBreakdown(formType, formData)}
        </div>
      </div>
    );
  };

  return (
    <>
      <PageTitle activeMenu="Staff Reporting" motherMenu="Workforce" />
      <div className="space-y-6">
        <section className={`${panelClass} p-6`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-700">
                Reporting detail
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <h1 className="text-3xl font-semibold text-gray-900">
                  {employeeName}
                </h1>
                {selectedReport && (
                  <span
                    className={`${formatStatusClass(selectedReport)} rounded-full px-3 py-1 text-xs font-semibold`}
                  >
                    {selectedReport.status || "Pending"}
                  </span>
                )}
              </div>
              <p className="mt-2 text-sm text-gray-500">
                {formatRoleLabel(employeeRole)}
              </p>
              {selectedReport &&
                (() => {
                  // The true last-edit date = timestamp of the newest history
                  // entry after the auto "Created …" record. lastEditedAt /
                  // updatedAt can't be trusted (default to creation, bumped by
                  // unrelated writes).
                  const edits = (
                    Array.isArray(selectedReport.history)
                      ? selectedReport.history
                      : []
                  ).slice(1);
                  const updatedAt = edits.length
                    ? edits[edits.length - 1]?.changedAt
                    : null;
                  return (
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                      {updatedAt ? (
                        <span className="font-semibold text-amber-700">
                          Last updated {formatDateLabel(updatedAt)}
                        </span>
                      ) : (
                        <span className="font-medium text-gray-400">
                          Not updated yet
                        </span>
                      )}
                      <span className="text-gray-400">
                        Created {formatDateLabel(selectedReport.reportDate)}
                      </span>
                    </div>
                  );
                })()}
            </div>
            <div className="flex items-center gap-2">
              {isSuperAdmin && Number(selectedReport?.formType) === 1 && (
                <button
                  type="button"
                  className="rounded-full border-2 border-amber-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={handleTransferWork}
                  disabled={saving}
                >
                  Transfer Work
                </button>
              )}
              <button
                type="button"
                className="rounded-full bg-amber-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={handleExportExcel}
                disabled={!selectedReport || exporting}
              >
                {exporting ? "Preparing..." : "Download Excel"}
              </button>
              <button
                type="button"
                className="rounded-full border-2 border-amber-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-gray-700"
                onClick={() =>
                  navigate(
                    employee?._id || selectedReport?.employeeId
                      ? `/staff-reporting/employee/${employee?._id || selectedReport.employeeId}`
                      : "/staff-reporting",
                  )
                }
              >
                Back to list
              </button>
            </div>
          </div>
        </section>

        {renderReadOnlyBlock()}
      </div>

      {remarksModal && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => {
            setRemarksModal(null);
            setUpdateEdit(null);
          }}
        >
          <div
            className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <h3 className="text-base font-semibold text-gray-900">
                {remarksModal.title}
              </h3>
              <button
                type="button"
                className="rounded-full border-2 border-gray-300 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-gray-600"
                onClick={() => {
                  setRemarksModal(null);
                  setUpdateEdit(null);
                }}
              >
                Close
              </button>
            </div>
            <div className="mt-4 max-h-96 space-y-2 overflow-y-auto">
              {remarksModalHistory.map((entry, i) => {
                const entryKey = `${entry.date}::${entry.peopleCount}::${entry.text}`;
                const isEditing = updateEdit?.key === entryKey;
                return (
                  <div
                    key={`${entryKey}-${i}`}
                    className={`rounded-lg px-3 py-2 text-sm ${
                      i % 2 === 1 ? "bg-gray-50" : "bg-white"
                    }`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <span className="font-semibold text-gray-700">
                          {formatDateLabel(entry.date)}
                        </span>
                        {!isEditing &&
                          entry.peopleCount !== "" &&
                          entry.peopleCount != null && (
                            <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600">
                              👥 {entry.peopleCount}
                            </span>
                          )}
                      </div>
                      {remarksModal.mainId && !isEditing && (
                        <div className="flex gap-3 text-[11px] font-semibold uppercase tracking-[0.14em]">
                          <button
                            type="button"
                            className="text-amber-700 hover:text-amber-800 disabled:opacity-50"
                            disabled={saving}
                            onClick={() =>
                              setUpdateEdit({
                                key: entryKey,
                                date: String(entry.date || "").slice(0, 10),
                                text: entry.text || "",
                                people:
                                  entry.peopleCount === "" ||
                                  entry.peopleCount == null
                                    ? ""
                                    : String(entry.peopleCount),
                              })
                            }
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="text-red-600 hover:text-red-700 disabled:opacity-50"
                            disabled={saving}
                            onClick={() =>
                              handleSaveWorkUpdate(entry, { remove: true })
                            }
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                    {isEditing ? (
                      <div className="mt-2 space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className={labelClass}>Date</label>
                            <input
                              type="date"
                              className={inputClass}
                              autoComplete="off"
                              value={updateEdit.date || ""}
                              max={toDateInputValue()}
                              onChange={(e) =>
                                setUpdateEdit((prev) => ({
                                  ...prev,
                                  date: e.target.value,
                                }))
                              }
                            />
                          </div>
                          <div>
                            <label className={labelClass}>People</label>
                            <input
                              type="number"
                              min="0"
                              className={inputClass}
                              autoComplete="off"
                              value={updateEdit.people}
                              onChange={(e) =>
                                setUpdateEdit((prev) => ({
                                  ...prev,
                                  people: e.target.value,
                                }))
                              }
                              placeholder="People"
                            />
                          </div>
                          <div className="col-span-2">
                            <label className={labelClass}>Note</label>
                            <input
                              className={inputClass}
                              autoComplete="off"
                              value={updateEdit.text}
                              onChange={(e) =>
                                setUpdateEdit((prev) => ({
                                  ...prev,
                                  text: e.target.value,
                                }))
                              }
                              placeholder="Progress note"
                            />
                          </div>
                        </div>
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            className="rounded-full border-2 border-gray-300 px-4 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-gray-600"
                            onClick={() => setUpdateEdit(null)}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="rounded-full bg-amber-500 px-4 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-950 hover:bg-amber-400 disabled:opacity-60"
                            disabled={saving}
                            onClick={() => handleSaveWorkUpdate(entry)}
                          >
                            {saving ? "Saving..." : "Save"}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-0.5 text-gray-700">
                        {entry.text || "—"}
                      </div>
                    )}
                  </div>
                );
              })}
              {remarksModalHistory.length === 0 && (
                <div className="px-3 py-6 text-center text-xs text-gray-400">
                  No updates logged yet.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DetailStaffReporting;
